import React, { useState, useEffect } from 'react';
import { Typography, Spin, Empty, Row, Col, Table, Card, Button, Radio, Tabs } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, LineChart, ComposedChart, Brush, Area, AreaChart, ReferenceLine, PieChart, Pie } from 'recharts';
import GlobalFilterBar from '../components/common/GlobalFilterBar';
import DownloadButton from '../components/common/DownloadButton';
import { useFilters } from '../context/FilterContext';
import { getDsmPenalty, getVolumeDashboard, getRevenueAnalysis } from '../services/api';
import { formatCurrencyLakhs, formatChartDateAxis, formatNumberKM, formatCurrencyKM, tooltipSorter, formatTooltipValue, getTableColumns, formatTableData, aggregateChartData as formattersAggregateChartData } from '../utils/formatters';
import { exportCardAsPdf, exportTableAsCsv, exportFullDashboardPdf, exportCardAsPng } from '../utils/downloadUtils';
import dayjs from 'dayjs';

const DSM_SUMMARY_BOX_BORDER = '1px solid #94a3b8';
const DSM_SUMMARY_CARD_HEAD_STYLE = {
  background: '#f1f5f9',
  borderBottom: '1px solid #cbd5e1',
  padding: '10px 16px',
  minHeight: 'auto'
};

const formatVolumeNumber = (val) => {
  const num = Number(val) || 0;
  if (Math.abs(num) >= 10000) {
    return `${(num / 1000000).toFixed(2)} MU`;
  }
  return `${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`;
};

function DonutChartWithLegend({ data, colors, title }) {
  const [isExchangeExpanded, setIsExchangeExpanded] = useState(false);

  const total = React.useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.value || 0), 0);
  }, [data]);

  const netTotal = React.useMemo(() => {
    return data.reduce((acc, curr) => acc + (curr.value || 0), 0);
  }, [data]);

  const pieChartData = React.useMemo(() => {
    return data.map(item => ({
      ...item,
      displayValue: Math.abs(item.value || 0)
    }));
  }, [data]);

  const sortedLegends = React.useMemo(() => {
    return [...data].sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
  }, [data]);

  const formatVolume = (val) => {
    return formatVolumeNumber(val);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0].payload;
      const val = Math.abs(entry.value || 0);
      const pct = total > 0 ? (val / total) * 100 : 0;
      const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
      const formattedVal = formatVolume(entry.value);

      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #e0e0e0',
          padding: '8px 12px',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '12px',
          color: '#333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key] || '#8884d8' }} />
            <span>{entry.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>Volume:</span>
            <span style={{ fontWeight: 600 }}>{formattedVal} ({pctString})</span>
          </div>
          {entry.breakdown && (
            <div style={{ borderTop: '1px solid #eaeaea', paddingTop: 6, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                  <span>GDAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.gdam)} ({entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                  <span>DAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.dam)} ({entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                  <span>RTM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatVolume(entry.breakdown.rtm)} ({entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 32, padding: '0 16px', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: '260px', height: 260, flex: '1 1 calc(50% - 16px)', minWidth: '220px' }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Total</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 2 }}>
            {formatVolume(netTotal)}
          </div>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieChartData}
              dataKey="displayValue"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={95}
              paddingAngle={2}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.key] || '#8884d8'} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1000 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={{ flex: '1 1 calc(50% - 16px)', minWidth: '220px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 8 }}>
        {sortedLegends.map((entry) => {
          const val = Math.abs(entry.value || 0);
          const pct = total > 0 ? (val / total) * 100 : 0;
          const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
          const formattedVal = formatVolume(entry.value);

          return (
            <div key={entry.key} style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, paddingBottom: 4, paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key], flexShrink: 0 }} />
                  <span style={{ color: '#333', fontWeight: 500 }}>{entry.name}</span>
                  {entry.breakdown && (
                    <Button
                      type="text"
                      size="small"
                      style={{ padding: '0 4px', height: '18px', fontSize: 10, display: 'inline-flex', alignItems: 'center', minWidth: 0, lineHeight: 1 }}
                      icon={isExchangeExpanded ? <MinusOutlined style={{ fontSize: 9 }} /> : <PlusOutlined style={{ fontSize: 9 }} />}
                      onClick={() => setIsExchangeExpanded(!isExchangeExpanded)}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#777', fontSize: 11 }}>{formattedVal}</span>
                  <span style={{ color: '#111', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{pctString}</span>
                </div>
              </div>

              {entry.breakdown && isExchangeExpanded && (
                <div style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                      <span>GDAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.gdam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                      <span>DAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.dam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                      <span>RTM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatVolume(entry.breakdown.rtm)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const formatDsmTooltipValue = (value, minimumFractionDigits = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const num = Number(value);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const fmt = (val) => val.toLocaleString('en-IN', {
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits
  });

  if (absNum >= 1000000) {
    return `${sign}₹ ${fmt(absNum / 1000000)} M`;
  }
  if (absNum >= 1000) {
    return `${sign}₹ ${fmt(absNum / 1000)} K`;
  }
  return `${sign}₹ ${fmt(absNum)}`;
};

const renderCustomLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      {payload.map((entry, index) => {
        if (entry.value === 'base' || entry.value === 'dummy' || entry.value === 'Deviation') return null;
        let color = entry.color;
        if (entry.value === 'Over-Injection (OI)') color = '#ef4444';
        if (entry.value === 'Under-Injection (UI)') color = '#0288D1';
        if (entry.value === 'Deviation') color = '#ccc';
        let isLine = entry.type === 'line';
        if (entry.value === 'Over-Injection (OI)' || entry.value === 'Under-Injection (UI)' || entry.value === 'Deviation') {
          isLine = false;
        }
        return (
          <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
            <span style={{
              display: 'inline-block',
              width: isLine ? 16 : 12,
              height: isLine ? 4 : 12,
              backgroundColor: color,
              marginRight: 6,
              borderRadius: isLine ? 0 : 2
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
};

const renderPenaltyLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      {payload.map((entry, index) => {
        let color = entry.color;
        return (
          <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
            <span style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              backgroundColor: color,
              marginRight: 6,
              borderRadius: 2
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
};


const formatAnalysisTooltipLabel = (label, granularity, payloadItem) => {
  if (granularity === '15min-raw') {
    const d = dayjs(label);
    return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : (label || '15-Min Raw');
  }
  if (granularity === '15min' || granularity === 'block') {
    const blockNum = payloadItem?.block || payloadItem?.sort_block || (payloadItem?.time_block_index != null ? payloadItem.time_block_index + 1 : null);
    let timeStr = payloadItem?.time_block;
    if (!timeStr && blockNum && blockNum >= 1 && blockNum <= 96) {
      const mins = (blockNum - 1) * 15;
      const h = String(Math.floor(mins / 60)).padStart(2, '0');
      const m = String(mins % 60).padStart(2, '0');
      timeStr = `${h}:${m}`;
    }
    if (blockNum && timeStr) {
      return `Block ${blockNum} (${timeStr})`;
    }
    if (timeStr && timeStr !== 'Entire time') {
      return `Time Block: ${timeStr}`;
    }
    if (label && label !== 'Entire time') {
      const num = Number(label);
      if (!isNaN(num) && num >= 1 && num <= 96) {
        const mins = (num - 1) * 15;
        const h = String(Math.floor(mins / 60)).padStart(2, '0');
        const m = String(mins % 60).padStart(2, '0');
        return `Block ${num} (${h}:${m})`;
      }
      return `Time: ${label}`;
    }
    return `15-Min Block Aggregate`;
  }
  if (granularity === 'hourly' || granularity === 'hour') {
    if (label && label !== 'Entire time') {
      return `Time: ${label}${label.includes(':') ? '' : ':00'}`;
    }
    return `Hourly Aggregate`;
  }
  const d = dayjs(label);
  if (d.isValid()) {
    if (granularity === 'monthly') return d.format('MMM-YYYY');
    if (granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM-YYYY');
  }
  return label || 'Summary';
};

const ScheduleVsActualTooltip = ({ active, payload, label, granularity, isHourlyOr15Min }) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0].payload;
  const sch = Number(dataPoint.schedule) || 0;
  const act = Number(dataPoint.actual) || 0;
  const dev = act - sch;

  const formattedLabel = formatAnalysisTooltipLabel(label, granularity, dataPoint);

  const formatVal = (v) => Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatDev = (v) => {
    const sign = v >= 0 ? '+' : '';
    return `${sign}${formatVal(v)}`;
  };

  const avc = Number(dataPoint.avc || dataPoint.available_capacity || dataPoint.avc_mw || dataPoint.capacity) || 0;
  const devPct = dataPoint.deviation_pct !== undefined
    ? Number(dataPoint.deviation_pct)
    : (avc !== 0 ? (dev / avc) * 100 : 0);
  const devPctStr = `${formatDev(devPct)}%`;

  const scheduleColor = '#0052CC';
  const actualColor = '#E74C3C';
  const deviationColor = '#888888';

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      color: '#333',
      lineHeight: '1.6'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid #f0f0f0', paddingBottom: '4px' }}>
        {formattedLabel}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '2px' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: scheduleColor }} />
        <span style={{ fontWeight: 500 }}>Scheduled Injection:</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600, paddingLeft: 12 }}>{formatVal(sch)} MW</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '2px' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: actualColor }} />
        <span style={{ fontWeight: 500 }}>Actual Injection:</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600, paddingLeft: 12 }}>{formatVal(act)} MW</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: deviationColor }} />
        <span style={{ fontWeight: 500 }}>Deviation:</span>
        <span style={{ marginLeft: 'auto', fontWeight: 600, paddingLeft: 12 }}>{devPctStr} ({formatDev(dev)} MW)</span>
      </div>
    </div>
  );
};

const dsmTableCols = [
  { title: 'Date', dataIndex: 'date', key: 'date', width: 110 },
  { title: 'Time Block', dataIndex: 'time_block', key: 'time_block', width: 120 },
  { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name', width: 150 },
  { title: 'Scheduled Vol (MW)', dataIndex: 'scheduled_vol_mw', key: 'scheduled_vol_mw', width: 150, render: v => Number(v || 0).toFixed(2) },
  { title: 'Actual Gen (MW)', dataIndex: 'actual_gen_mw', key: 'actual_gen_mw', width: 150, render: v => Number(v || 0).toFixed(2) },
  { title: 'Deviation (MW)', dataIndex: 'deviation_mw', key: 'deviation_mw', width: 130, render: v => Number(v || 0).toFixed(2) },
  {
    title: 'UI/OI Flag', dataIndex: 'ui_oi_flag', key: 'ui_oi_flag', width: 130, render: v => (
      <span style={{ color: v === 'Overinjection' ? '#27AE60' : v === 'Underinjection' ? '#E74C3C' : '#333', fontWeight: 600 }}>
        {v}
      </span>
    )
  },
  { title: 'Payable (₹)', dataIndex: 'dsm_payable', key: 'dsm_payable', width: 120, render: v => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
  { title: 'Receivable (₹)', dataIndex: 'dsm_receivable', key: 'dsm_receivable', width: 120, render: v => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
  { title: 'Penalty (₹)', dataIndex: 'dsm_penalty', key: 'dsm_penalty', width: 120, render: v => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
];

function KpiCardV2({ title, value, prevValue, delta, suffix, isCurrency, color }) {
  const isPos = delta > 0;
  const dColor = isPos ? '#27AE60' : delta < 0 ? '#E74C3C' : '#888';
  const fmtVal = isCurrency ? formatCurrencyLakhs(value) : (value != null ? Number(value).toFixed(2) : '-');
  const fmtPrev = isCurrency ? formatCurrencyLakhs(prevValue) : (prevValue != null ? Number(prevValue).toFixed(2) : '-');
  return (
    <Card size="small" style={{ background: color || '#f0f3ff', borderRadius: 8, height: '100%', width: '100%', padding: '4px 8px' }} bodyStyle={{ padding: 0 }}>
      <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 600, display: 'block', height: '35px', overflow: 'hidden' }}>{title}</Typography.Text>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1B4F72', marginTop: 2, whiteSpace: 'nowrap' }}>
        {fmtVal}{suffix ? <span style={{ fontSize: 12, fontWeight: 400 }}> {suffix}</span> : null}
      </div>
      {/* <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
        {delta != null && delta !== 0 && <span style={{ color: dColor, fontSize: 10, fontWeight: 600 }}>
          {isPos ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(delta)}%
        </span>}
        <span style={{ color: '#999', fontSize: 10 }}>vs PY: {fmtPrev}</span>
      </div> */}
    </Card>
  );
}


const aggregateDsmChartData = (data, granularity) => {
  if (granularity !== 'hourly' && granularity !== '15min') return data;

  let minMins = 0;
  let maxMins = 24 * 60 - 1;

  const encounteredMins = [];
  data.forEach(item => {
    if (!item.period) return;
    let timeStr;
    if (typeof item.period === 'string' && item.period.includes(' ')) {
      timeStr = item.period.split(' ')[1];
    } else if (typeof item.period === 'string' && item.period.includes(':')) {
      timeStr = item.period;
    } else {
      return;
    }

    let mins;
    if (granularity === 'hourly') {
      const h = parseInt(timeStr.split(':')[0], 10);
      if (!isNaN(h)) mins = h * 60;
    } else {
      const parts = timeStr.split(':');
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h) && !isNaN(m)) mins = h * 60 + m;
    }
    if (mins !== undefined) {
      encounteredMins.push(mins);
    }
  });

  if (encounteredMins.length > 0) {
    minMins = Math.min(...encounteredMins);
    maxMins = Math.max(...encounteredMins);
  }

  const grouped = {};

  if (granularity === 'hourly') {
    const startHour = Math.floor(minMins / 60);
    const endHour = Math.floor(maxMins / 60);
    for (let i = startHour; i <= endHour; i++) {
      const h = String(i).padStart(2, '0');
      grouped[h] = { period: h };
    }
  } else if (granularity === '15min') {
    for (let mins = minMins; mins <= maxMins; mins += 15) {
      const h = String(Math.floor(mins / 60)).padStart(2, '0');
      const m = String(mins % 60).padStart(2, '0');
      const timeStr = `${h}:${m}`;
      const block_number = Math.floor(mins / 15) + 1;
      grouped[timeStr] = { period: timeStr, block_number };
    }
  }

  const keysToSum = [
    'oi_0_12', 'oi_12_15', 'oi_15_20', 'oi_20_plus',
    'ui_0_12', 'ui_12_15', 'ui_15_20', 'ui_20_plus',
    'oi_penalty', 'ui_penalty',
    'cum_sum',
    'seg_gt_20', 'seg_15_20', 'seg_12_15', 'seg_safe', 'seg_neg_12_15', 'seg_neg_15_20', 'seg_lt_neg_20',
    'schedule', 'actual', 'avc',
    'base', 'overInjection', 'underInjection',
    'receivable', 'payable', 'net_rev_cum'
  ];

  Object.values(grouped).forEach(g => {
    keysToSum.forEach(k => g[k] = 0);
    g.count = 0;
  });

  data.forEach(item => {
    if (!item.period) return;
    let timeStr;
    if (typeof item.period === 'string' && item.period.includes(' ')) {
      timeStr = item.period.split(' ')[1];
    } else if (typeof item.period === 'string' && item.period.includes(':')) {
      timeStr = item.period;
    } else {
      return;
    }

    if (granularity === 'hourly') {
      timeStr = timeStr.split(':')[0];
    }

    if (grouped[timeStr]) {
      if (!grouped[timeStr].date) {
        if (typeof item.period === 'string' && item.period.includes(' ')) {
          grouped[timeStr].date = item.period.split(' ')[0];
        } else if (item.date) {
          grouped[timeStr].date = item.date;
        }
      }
      keysToSum.forEach(k => {
        grouped[timeStr][k] += (Number(item[k]) || 0);
      });
      grouped[timeStr].count += 1;
    }
  });

  Object.values(grouped).forEach(g => {
    if (g.count > 0) {
      g.schedule = g.schedule / g.count;
      g.actual = g.actual / g.count;
      g.avc = g.avc / g.count;
    }
  });

  const sortedKeys = Object.keys(grouped).sort();
  return sortedKeys.map(k => grouped[k]);
};

function DsmSummaryTab() {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  const yesterdayDateLabel = dayjs().subtract(1, 'day').format('DD-MM-YYYY');

  const [yesterdaySegData, setYesterdaySegData] = useState([]);
  const [segViewMode, setSegViewMode] = useState('chart');

  useEffect(() => {
    let isMounted = true;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const mtdStart = yesterday.startOf('month');
        const ytdStart = yesterday.startOf('year');

        const [resYesterday, resMtd, resYtd] = await Promise.all([
          getDsmPenalty({ date_from: yesterday.format('YYYY-MM-DD'), date_to: yesterday.format('YYYY-MM-DD') }).catch(() => null),
          getDsmPenalty({ date_from: mtdStart.format('YYYY-MM-DD'), date_to: yesterday.format('YYYY-MM-DD') }).catch(() => null),
          getDsmPenalty({ date_from: ytdStart.format('YYYY-MM-DD'), date_to: yesterday.format('YYYY-MM-DD') }).catch(() => null),
        ]);

        const parseYesterdaySeg = (res) => {
          if (res?.data?.energy_segregation) {
            const seg = res.data.energy_segregation;
            const seg_gt_20 = Number(seg.seg_gt_20 || 0);
            const seg_15_20 = Number(seg.seg_15_20 || 0);
            const seg_12_15 = Number(seg.seg_12_15 || 0);
            const seg_safe = Number(seg.seg_safe || 0);
            const seg_neg_12_15 = Number(seg.seg_neg_12_15 || 0);
            const seg_neg_15_20 = Number(seg.seg_neg_15_20 || 0);
            const seg_lt_neg_20 = Number(seg.seg_lt_neg_20 || 0);
            const totalScheduleKwh = Number(seg.total_schedule_kwh || 0);

            const val_gt_20 = seg_gt_20 / 1000.0;
            const val_15_20 = seg_15_20 / 1000.0;
            const val_12_15 = seg_12_15 / 1000.0;
            const val_safe = seg_safe / 1000.0;
            const val_neg_12_15 = seg_neg_12_15 / 1000.0;
            const val_neg_15_20 = seg_neg_15_20 / 1000.0;
            const val_lt_neg_20 = seg_lt_neg_20 / 1000.0;

            return [
              { name: '> 20%', value: val_gt_20, pct: totalScheduleKwh > 0 ? (seg_gt_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
              { name: '15% to 20%', value: val_15_20, pct: totalScheduleKwh > 0 ? (seg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
              { name: '12% to 15%', value: val_12_15, pct: totalScheduleKwh > 0 ? (seg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
              { name: '-12% to 12%', value: val_safe, pct: totalScheduleKwh > 0 ? (seg_safe / totalScheduleKwh * 100) : 0, color: '#27AE60' },
              { name: '-15% to -12%', value: val_neg_12_15, pct: totalScheduleKwh > 0 ? (seg_neg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
              { name: '-20% to -15%', value: val_neg_15_20, pct: totalScheduleKwh > 0 ? (seg_neg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
              { name: '< -20%', value: val_lt_neg_20, pct: totalScheduleKwh > 0 ? (seg_lt_neg_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            ];
          }

          const rows = res?.data?.chart_data || [];
          let seg_gt_20 = 0;
          let seg_15_20 = 0;
          let seg_12_15 = 0;
          let seg_safe = 0;
          let seg_neg_12_15 = 0;
          let seg_neg_15_20 = 0;
          let seg_lt_neg_20 = 0;
          let totalScheduleKwh = 0;

          rows.forEach((d) => {
            seg_gt_20 += Number(d.oi_20_plus || 0);
            seg_15_20 += Number(d.oi_15_20 || 0);
            seg_12_15 += Number(d.oi_12_15 || 0);
            seg_safe += (Number(d.oi_0_12 || 0) + Math.abs(Number(d.ui_0_12 || 0)));
            seg_neg_12_15 += Math.abs(Number(d.ui_12_15 || 0));
            seg_neg_15_20 += Math.abs(Number(d.ui_15_20 || 0));
            seg_lt_neg_20 += Math.abs(Number(d.ui_20_plus || 0));

            const schedKwh = d.total_schedule_kwh != null
              ? Number(d.total_schedule_kwh)
              : (d.schedule != null ? Number(d.schedule) * 250.0 : 0);
            totalScheduleKwh += schedKwh;
          });

          // Convert values from kWh (Units) to MWh
          const val_gt_20 = seg_gt_20 / 1000.0;
          const val_15_20 = seg_15_20 / 1000.0;
          const val_12_15 = seg_12_15 / 1000.0;
          const val_safe = seg_safe / 1000.0;
          const val_neg_12_15 = seg_neg_12_15 / 1000.0;
          const val_neg_15_20 = seg_neg_15_20 / 1000.0;
          const val_lt_neg_20 = seg_lt_neg_20 / 1000.0;

          return [
            { name: '> 20%', value: val_gt_20, pct: totalScheduleKwh > 0 ? (seg_gt_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            { name: '15% to 20%', value: val_15_20, pct: totalScheduleKwh > 0 ? (seg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            { name: '12% to 15%', value: val_12_15, pct: totalScheduleKwh > 0 ? (seg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            { name: '-12% to 12%', value: val_safe, pct: totalScheduleKwh > 0 ? (seg_safe / totalScheduleKwh * 100) : 0, color: '#27AE60' },
            { name: '-15% to -12%', value: val_neg_12_15, pct: totalScheduleKwh > 0 ? (seg_neg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            { name: '-20% to -15%', value: val_neg_15_20, pct: totalScheduleKwh > 0 ? (seg_neg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
            { name: '< -20%', value: val_lt_neg_20, pct: totalScheduleKwh > 0 ? (seg_lt_neg_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
          ];
        };

        const parseDsmData = (res) => {
          const rows = res?.data?.chart_data || [];
          if (!rows || rows.length === 0) {
            return {
              volPct: '0.00%',
              penaltyRs: '₹ 0',
              penaltyPerKwh: '₹ 0.00 / kWh',
              oiUiRatio: '0.00'
            };
          }

          let totPenalty = 0;
          let totActualGenKwh = 0;
          let totDevVol = 0;
          let totSchedKwh = 0;
          let totOiSlabs = 0;
          let totUiSlabs = 0;

          rows.forEach(r => {
            const p = r.total_penalty != null
              ? Number(r.total_penalty)
              : ((Number(r.oi_penalty) || 0) + (Number(r.ui_penalty) || 0));

            const actMw = Number(r.actual != null ? r.actual : r.actual_generation_mw) || 0;
            const actGenKwh = r.total_actual_gen_kwh != null
              ? Number(r.total_actual_gen_kwh)
              : (actMw * 250.0);

            totPenalty += p;
            totActualGenKwh += actGenKwh;

            const devVol = r.deviated_vol != null
              ? Number(r.deviated_vol)
              : (r.deviation_mw != null ? Math.abs(Number(r.deviation_mw)) * 0.25 : 0);
            const schedKwh = r.total_schedule_kwh != null
              ? Number(r.total_schedule_kwh)
              : (r.schedule != null ? Number(r.schedule) * 250.0 : 0);

            totDevVol += devVol;
            totSchedKwh += schedKwh;

            const oiSlab = Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0);
            const uiSlab = Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);

            totOiSlabs += oiSlab;
            totUiSlabs += uiSlab;
          });

          // 1. Penalty (Rs./kWh) = sum(total_penalty) / (sum(actual_generation_mw) * 250)
          const penaltyPerKwh = totActualGenKwh > 0 ? (totPenalty / totActualGenKwh) : 0;

          // 2. Volume % in Penalty Band = Absolute sum of volumes across all penalty bands, excluding the Free Band, divided by Schedule
          const volPenaltyBandsSum = Math.abs(totOiSlabs) + Math.abs(totUiSlabs);
          const volPct = totSchedKwh > 0 ? (volPenaltyBandsSum / totSchedKwh) * 100.0 : 0;

          // 3. OI / UI Ratio = Sum of volumes across all OI slabs, excluding the Free Band, divided by the sum of volumes across all UI slabs, excluding the Free Band
          let oiUiRatio = '0.00';
          if (totUiSlabs > 0) {
            oiUiRatio = (totOiSlabs / totUiSlabs).toFixed(2);
          } else if (totOiSlabs === 0) {
            oiUiRatio = '0.00';
          } else {
            oiUiRatio = '0.00';
          }

          // Rule: For values < 10,000 show as full, rest divide by M and add M in front (e.g. ₹ 0.45 M)
          const formatPenaltyValue = (val) => {
            if (val == null || isNaN(val)) return '-';
            const num = Number(val);
            const absVal = Math.abs(num);
            const sign = num < 0 ? '-' : '';
            if (absVal < 10000) {
              return `${sign}₹ ${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
            } else {
              const inM = absVal / 1000000.0;
              const formatted = inM >= 10 ? inM.toFixed(1) : inM.toFixed(2);
              return `${sign}₹ ${formatted} M`;
            }
          };

          return {
            volPct: `${volPct.toFixed(2)}%`,
            penaltyRs: formatPenaltyValue(totPenalty),
            penaltyPerKwh: `₹ ${penaltyPerKwh.toFixed(2)} / kWh`,
            oiUiRatio: oiUiRatio
          };
        };

        if (isMounted) {
          setSummaryData({
            yesterday: parseDsmData(resYesterday),
            mtd: parseDsmData(resMtd),
            ytd: parseDsmData(resYtd),
          });
          setYesterdaySegData(parseYesterdaySeg(resYesterday));
        }
      } catch (err) {
        console.error("Error fetching DSM summary data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSummary();
    return () => { isMounted = false; };
  }, []);

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '12px 0 24px 0' }}>
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} xl={12} style={{ display: 'flex' }}>
            <div
              style={{
                background: '#ffffff',
                border: DSM_SUMMARY_BOX_BORDER,
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  ...DSM_SUMMARY_CARD_HEAD_STYLE,
                  fontWeight: 700,
                  fontSize: 14,
                  color: '#1B4F72',
                }}
              >
                DSM Summary
              </div>
              <div style={{ flex: 1, overflowX: 'auto', display: 'flex' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, height: '100%' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e8f1f9', borderBottom: '1px solid #cbd5e1' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#1B4F72', width: '35%', borderRight: '1px solid #cbd5e1' }}>
                        Parameter
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#1B4F72', width: '22%', borderRight: '1px solid #cbd5e1' }}>
                        {yesterdayDateLabel}
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#1B4F72', width: '21%', borderRight: '1px solid #cbd5e1' }}>
                        MTD
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#1B4F72', width: '22%' }}>
                        YTD
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                        Volume % in penalty band
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.yesterday.volPct : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.mtd.volPct : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500 }}>
                        {summaryData ? summaryData.ytd.volPct : '-'}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                        Penalty (Rs.)
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.yesterday.penaltyRs : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.mtd.penaltyRs : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500 }}>
                        {summaryData ? summaryData.ytd.penaltyRs : '-'}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                        Penalty (Rs./kWh)
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.yesterday.penaltyPerKwh : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.mtd.penaltyPerKwh : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500 }}>
                        {summaryData ? summaryData.ytd.penaltyPerKwh : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155', borderRight: '1px solid #cbd5e1' }}>
                        OI / UI Ratio
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.yesterday.oiUiRatio : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: '1px solid #cbd5e1' }}>
                        {summaryData ? summaryData.mtd.oiUiRatio : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: '#0f172a', fontWeight: 500 }}>
                        {summaryData ? summaryData.ytd.oiUiRatio : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Col>

          <Col xs={24} xl={12} style={{ display: 'flex' }}>
            <Card
              id="chart-summary-yesterday-energy-seg"
              bordered={true}
              headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
              title={<span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Distribution of Deviated Units ({yesterdayDateLabel})</span>}
              size="small"
              style={{
                borderRadius: 8,
                background: '#fff',
                border: DSM_SUMMARY_BOX_BORDER,
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ flex: 1, padding: '10px 16px 10px 10px', display: 'flex', flexDirection: 'column' }}
              extra={
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Radio.Group value={segViewMode} onChange={e => setSegViewMode(e.target.value)} size="small">
                    <Radio.Button value="chart">Chart</Radio.Button>
                    <Radio.Button value="table">Table</Radio.Button>
                  </Radio.Group>
                  {segViewMode === 'chart' ? (
                    <DownloadButton onPng={() => exportCardAsPng('chart-summary-yesterday-energy-seg', `Distribution_of_Deviated_Units_${yesterdayDateLabel}`)} />
                  ) : (
                    <DownloadButton onCsv={() => exportTableAsCsv(yesterdaySegData, [
                      { title: 'Segregation', dataIndex: 'name', key: 'name', render: text => <strong>{text}</strong> },
                      { title: 'Value (MWh)', dataIndex: 'value', key: 'value', render: v => v != null ? `${Number(v).toFixed(2)} MWh` : '-' },
                      { title: 'Percentage (%)', dataIndex: 'pct', key: 'pct', render: v => v != null ? `${Number(v).toFixed(2)}%` : '-' },
                    ], `Distribution_of_Deviated_Units_${yesterdayDateLabel}`)} />
                  )}
                </div>
              }
            >
              {segViewMode === 'chart' ? (
                <div style={{ height: 260, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={yesterdaySegData}
                      layout="vertical"
                      margin={{ top: 5, right: 80, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis
                        type="number"
                        tickFormatter={(v) => formatNumberKM(v).toLowerCase()}
                        label={{ value: 'Deviation in MWh', position: 'insideBottom', offset: -12, style: { fontSize: 11 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fill: '#4b5563', fontWeight: 600, fontSize: 11 }}
                        width={85}
                      />
                      <Tooltip
                        formatter={(v, name, entry) => {
                          const item = entry.payload;
                          return [`${Number(v).toFixed(2)} MWh (${item.pct.toFixed(2)}%)`, 'Energy'];
                        }}
                      />
                      <Bar
                        dataKey="value"
                        barSize={18}
                        label={(props) => {
                          const { x, y, width, height, index } = props;
                          const item = yesterdaySegData[index];
                          if (!item) return null;
                          const text = `${item.value.toFixed(2)} (${item.pct.toFixed(2)}%)`;
                          const isInside = width > 110;
                          const textX = isInside ? x + width - 8 : x + width + 8;
                          const textAnchor = isInside ? 'end' : 'start';
                          const textColor = isInside ? '#ffffff' : '#333333';
                          return (
                            <text
                              x={textX}
                              y={y + height / 2}
                              fill={textColor}
                              textAnchor={textAnchor}
                              dominantBaseline="middle"
                              style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}
                            >
                              {text}
                            </text>
                          );
                        }}
                      >
                        {yesterdaySegData.map((entry, index) => (
                          <Cell key={`cell-seg-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table
                    dataSource={yesterdaySegData.map((item, idx) => ({ ...item, key: `seg_${idx}` }))}
                    columns={[
                      { title: 'Segregation', dataIndex: 'name', key: 'name', render: text => <strong>{text}</strong> },
                      { title: 'Value (MWh)', dataIndex: 'value', key: 'value', render: v => v != null ? `${Number(v).toFixed(2)} MWh` : '-' },
                      { title: 'Percentage (%)', dataIndex: 'pct', key: 'pct', render: v => v != null ? `${Number(v).toFixed(2)}%` : '-' },
                    ]}
                    rowKey="key"
                    size="small"
                    pagination={false}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 1st Graph: % volume deviated in the last 3 years (continuous series) */}
        <ThreeYearsDeviatedVolumeCard />

        {/* 2nd Graph: Penalty in Rs. (in million) and per unit (Rs./Kwh) for last year */}
        <PenaltyAndPerUnitLastYearCard />

        {/* 3rd Graph: % volume deviation for the last 30 days (daily average) */}
        <Last30DaysDeviatedVolumeCard />

        {/* 4th Graph: Per unit (Rs./scheduled) for the last 30 days (daily average) */}
        <Last30DaysPenaltyPerUnitCard />

        {/* 5th Graph: OI/UI ratio (last 15 days) */}
        <OiUiRatioLast15DaysCard />

        {/* 6th Graph: % of deviation for each of the 96 time blocks (7 days) */}
        <DeviatedVolume96Blocks7DaysCard />

        {/* 7th Graph: OI/UI ratio for each of the 96 time blocks (7 days) */}
        <OiUiRatio96Blocks7DaysCard />

        {/* Plant Actual vs Scheduled & Morning Forecast */}
        <YesterdayActualSchedMfCard />

      </div>
    </Spin>
  );
}

function YesterdayActualSchedMfCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const yesterdayStr = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
        const res = await getRevenueAnalysis({
          date_from: yesterdayStr,
          date_to: yesterdayStr,
          granularity: '15min'
        }).catch(() => null);

        const rows = res?.data?.results || res?.results || res || [];
        const yestBlocks = [];
        for (let b = 1; b <= 96; b++) {
          const totalMins = (b - 1) * 15;
          const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
          const m = String(totalMins % 60).padStart(2, '0');
          const timeStr = `${h}:${m}`;

          const endMins = b * 15;
          const endH = String(Math.floor(endMins / 60)).padStart(2, '0');
          const endM = String(endMins % 60).padStart(2, '0');
          const timeInterval = `${timeStr} - ${endH}:${endM}`;

          const existing = rows.find(r => {
            if (Number(r.block) === b || Number(r.time_block) === b) return true;
            if (r.period) {
              const pTime = r.period.includes(' ') ? r.period.split(' ')[1] : r.period;
              return pTime.startsWith(timeStr);
            }
            return false;
          });

          const act = Number(existing?.total_generation_mw) || 0;
          const sched = Number(existing?.total_schedule_mw) || 0;
          const mfRaw = Number(existing?.morning_forecast) || 0;
          const mfScaled = mfRaw > 0 ? (mfRaw * (106.4 / 56.0)) : 0;

          yestBlocks.push({
            block: b,
            time: timeStr,
            timeInterval,
            actual: act,
            schedule: sched,
            morning_forecast_raw: mfRaw,
            morning_forecast_scaled: mfScaled,
          });
        }
        if (isMounted) {
          setChartData(yestBlocks);
        }
      } catch (err) {
        console.error("Error fetching yesterday actual vs scheduled & MF data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-yest-actual-sched-mf';
  const yesterdayFormatted = dayjs().subtract(1, 'day').format('DD-MM-YYYY');

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 16, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
            Actual vs Scheduled & Morning Forecast ({yesterdayFormatted})
          </span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Yesterday_Actual_vs_Scheduled_MF_${dayjs().subtract(1, 'day').format('DDMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, [
              { title: 'Block', dataIndex: 'block', key: 'block' },
              { title: 'Time', dataIndex: 'timeInterval', key: 'timeInterval' },
              { title: 'Actual Generation (MW)', dataIndex: 'actual', key: 'actual', render: v => `${Number(v).toFixed(2)} MW` },
              { title: 'Scheduled Generation (MW)', dataIndex: 'schedule', key: 'schedule', render: v => `${Number(v).toFixed(2)} MW` },
              { title: 'Morning Forecast (OA 56 MW Raw)', dataIndex: 'morning_forecast_raw', key: 'morning_forecast_raw', render: v => `${Number(v).toFixed(2)} MW` },
              { title: 'Morning Forecast (Upscaled 106.4 MW)', dataIndex: 'morning_forecast_scaled', key: 'morning_forecast_scaled', render: v => `${Number(v).toFixed(2)} MW` },
            ], `Yesterday_Actual_vs_Scheduled_MF_${dayjs().subtract(1, 'day').format('DDMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 120px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ left: 20, right: 30, bottom: 20, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={true} stroke="#e2e8f0" />
                <XAxis
                  dataKey="time"
                  interval={3}
                  height={35}
                  tickLine={true}
                  stroke="#94a3b8"
                  tick={{ fontSize: 10, fill: '#475569', dy: 6 }}
                  tickFormatter={(v) => v}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(1)} MW`}
                  label={{
                    value: 'Generation & Forecast (MW)',
                    angle: -90,
                    position: 'insideLeft',
                    dx: -12,
                    dy: 15,
                    style: { fontSize: 11, fill: '#64748b', textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }}
                  labelFormatter={(label, payload) => {
                    const fullTime = payload?.[0]?.payload?.timeInterval || label;
                    return `Time: ${fullTime}`;
                  }}
                  formatter={(val, name) => {
                    return [`${Number(val).toFixed(2)} MW`, name];
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                {/* Actual Generation: Solid Emerald Line */}
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Actual Generation (MW)"
                  stroke="#10B981"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
                {/* Scheduled Generation: Solid Royal Blue Line */}
                <Line
                  type="monotone"
                  dataKey="schedule"
                  name="Scheduled Generation (MW)"
                  stroke="#2563EB"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
                {/* Morning Forecast: Dotted Purple Line (Upscaled to 106.4 MW) */}
                <Line
                  type="monotone"
                  dataKey="morning_forecast_scaled"
                  name="Morning Forecast"
                  stroke="#8B5CF6"
                  strokeWidth={2.4}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={[
              { title: 'Block', dataIndex: 'block', key: 'block', width: 70 },
              { title: 'Time', dataIndex: 'timeInterval', key: 'timeInterval' },
              { title: 'Actual Generation (MW)', dataIndex: 'actual', key: 'actual', render: v => `${Number(v).toFixed(2)}` },
              { title: 'Scheduled Generation (MW)', dataIndex: 'schedule', key: 'schedule', render: v => `${Number(v).toFixed(2)}` },
              { title: 'Morning Forecast (OA 56 MW Raw)', dataIndex: 'morning_forecast_raw', key: 'morning_forecast_raw', render: v => `${Number(v).toFixed(2)} MW` },
              { title: 'Morning Forecast (Upscaled 106.4 MW)', dataIndex: 'morning_forecast_scaled', key: 'morning_forecast_scaled', render: v => `${Number(v).toFixed(2)}` },
            ]}
            size="small"
            scroll={{ x: 'max-content', y: 300 }}
            pagination={{ pageSize: 24, showSizeChanger: true, pageSizeOptions: ['24', '48', '96'] }}
            rowKey="block"
          />
        )}
      </Spin>
    </Card>
  );
}

function ThreeYearsDeviatedVolumeCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch3YearDevData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const startYear = today.year() - 2;
        const dateFrom = `${startYear}-01-01`;
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: 'monthly'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const list = [];
        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            if (!r.period) return;
            const monthDate = dayjs(r.period);
            if (!monthDate.isValid() || monthDate.isAfter(yesterday, 'month')) return;

            const monthLabel = `${monthNames[monthDate.month()]}-${monthDate.format('YY')}`;
            const totalSched = r.total_schedule != null
              ? Number(r.total_schedule)
              : (r.schedule != null ? Number((Number(r.schedule) * 0.25 * 2880 / 1000).toFixed(2)) : null);

            // Total scheduled units (kWh)
            let totalSchedKwh = r.total_schedule_kwh != null
              ? Number(r.total_schedule_kwh)
              : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

            if (totalSchedKwh == null && r.schedule != null) {
              const daysInMonth = monthDate.daysInMonth();
              totalSchedKwh = Number(r.schedule) * 250.0 * 96 * daysInMonth;
            }

            // Sum of volumes across all bands, excluding the Free Band (in kWh)
            const devVolKwh = Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0)
              + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);

            // Actual Generation (MW)
            const actMw = Number(r.actual != null ? r.actual : r.actual_generation_mw) || 0;

            // % Volume Deviation = Sum of volumes across all bands, excluding the Free Band, divided by Total Schedule
            const devPct = totalSchedKwh && totalSchedKwh > 0 ? (devVolKwh / totalSchedKwh) * 100 : null;

            // Deviated Volume in MU (devVolKwh / 1,000,000)
            const devVolMu = devVolKwh / 1000000.0;

            if (devPct !== null) {
              list.push({
                period: r.period,
                monthLabel,
                devPct: Number(devPct.toFixed(2)),
                totalSched: totalSched != null ? Number(totalSched.toFixed(2)) : null,
                devVol: Number(devVolMu.toFixed(3)),
                actualMw: Number(actMw.toFixed(2)),
              });
            }
          });
        }

        if (isMounted) {
          setChartData(list);
        }
      } catch (err) {
        console.error("Error fetching 3-year deviation volume data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch3YearDevData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-3yr-deviated-volume';

  const tableColumns = [
    { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel', render: v => <strong>{v}</strong> },
    { title: '% Volume Deviated in Penalty Band', dataIndex: 'devPct', key: 'devPct', render: v => `${v}%` },
    { title: 'Total Schedule (MU)', dataIndex: 'totalSched', key: 'totalSched', render: v => `${v} MU` },
    { title: 'Deviated Volume (MU)', dataIndex: 'devVol', key: 'devVol', render: v => `${v} MU` },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>% Volume Deviated in Penalty Band (Last 3 Years)</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Percent_Volume_Deviated_3Years_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Percent_Volume_Deviated_3Years_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="monthLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={55}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${v}%`}
                  domain={[0, 'auto']}
                  label={{
                    value: '% Volume Deviated in Penalty Band',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 120,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                  formatter={(val) => [`${val}%`, '% Volume Deviated in Penalty Band']}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="monthLabel" height={22} stroke="#888" />
                <Area
                  type="monotone"
                  dataKey="devPct"
                  name="% Volume Deviated in Penalty Band"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function PenaltyAndPerUnitLastYearCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchLastYearPenaltyData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        // Fetch last 12 months up to current month (cut off at yesterday for MTD alignment)
        const dateFrom = today.subtract(11, 'month').startOf('month').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: 'monthly'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const list = [];
        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            if (!r.period) return;
            const monthDate = dayjs(r.period);
            if (!monthDate.isValid() || monthDate.isAfter(yesterday, 'month')) return;

            const monthLabel = `${monthNames[monthDate.month()]}-${monthDate.format('YY')}`;

            // Total penalty in Rs.
            const totalPenalty = r.total_penalty != null
              ? Number(r.total_penalty)
              : ((Number(r.oi_penalty) || 0) + (Number(r.ui_penalty) || 0));
            const penaltyInMillion = Number((totalPenalty / 1000000.0).toFixed(3));

            // Total scheduled units (1 unit = 1 kWh)
            let totalSchedKwh = r.total_schedule_kwh != null
              ? Number(r.total_schedule_kwh)
              : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

            if (totalSchedKwh == null && r.schedule != null) {
              const daysInMonth = monthDate.daysInMonth();
              totalSchedKwh = Number(r.schedule) * 250.0 * 96 * daysInMonth;
            }

            const totalSchedMu = totalSchedKwh != null ? totalSchedKwh / 1000000.0 : 0;

            // Total actual generation units (1 unit = 1 kWh)
            let totalActualKwh = r.total_actual_gen_kwh != null
              ? Number(r.total_actual_gen_kwh)
              : (r.actual != null ? Number(r.actual) * 250.0 * 96 * monthDate.daysInMonth() : null);

            const totalActualMu = totalActualKwh != null ? totalActualKwh / 1000000.0 : 0;

            // Penalty per unit = Total Penalty (Rs.) / Total Actual Units (kWh)
            const penaltyPerUnit = totalActualKwh && totalActualKwh > 0
              ? Number((totalPenalty / totalActualKwh).toFixed(3))
              : 0;

            list.push({
              period: r.period,
              monthLabel,
              totalPenalty: Number(totalPenalty.toFixed(2)),
              penaltyInMillion: Number(penaltyInMillion.toFixed(2)),
              totalActualKwh: Number((totalActualKwh || 0).toFixed(2)),
              totalActualMu: Number(totalActualMu.toFixed(2)),
              totalScheduleKwh: Number((totalSchedKwh || 0).toFixed(2)),
              totalScheduleMu: Number(totalSchedMu.toFixed(2)),
              penaltyPerUnit: Number(penaltyPerUnit.toFixed(3)),
            });
          });
        }

        if (isMounted) {
          setChartData(list);
        }
      } catch (err) {
        console.error("Error fetching last year penalty data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchLastYearPenaltyData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-last-year-penalty-per-unit';

  const tableColumns = [
    { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel', render: v => <strong>{v}</strong> },
    { title: 'Penalty in Rs. (in million)', dataIndex: 'penaltyInMillion', key: 'penaltyInMillion', render: v => `₹ ${Number(v).toFixed(2)} M` },
    { title: 'Total Penalty (Rs.)', dataIndex: 'totalPenalty', key: 'totalPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
    { title: 'Per Unit (Rs./kWh)', dataIndex: 'penaltyPerUnit', key: 'penaltyPerUnit', render: v => `₹ ${Number(v).toFixed(4)}` },
    { title: 'Total Actual (MU)', dataIndex: 'totalActualMu', key: 'totalActualMu', render: v => `${Number(v).toFixed(2)} MU` },
    { title: 'Actual Units (kWh)', dataIndex: 'totalActualKwh', key: 'totalActualKwh', render: v => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) },
    { title: 'Total Scheduled (MU)', dataIndex: 'totalScheduleMu', key: 'totalScheduleMu', render: v => `${Number(v).toFixed(2)} MU` },
    { title: 'Scheduled Units (kWh)', dataIndex: 'totalScheduleKwh', key: 'totalScheduleKwh', render: v => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Penalty and Per Unit DSM (Last year)</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Penalty_and_Per_Unit_Last_Year_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Penalty_and_Per_Unit_Last_Year_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ left: 15, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="monthLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={55}
                />
                {/* Left Y-axis (y1): Penalty in Rs. (in million) */}
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹ ${v} M`}
                  domain={[0, 'auto']}
                  label={{
                    value: 'Penalty (M)',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 70,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                {/* Right Y-axis (y2): Per Unit (Rs./kWh) */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹ ${Number(v).toFixed(2)}`}
                  domain={[0, 'auto']}
                  label={{
                    value: 'Per Unit (Rs./kWh)',
                    angle: 90,
                    position: 'insideRight',
                    dy: 70,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label) => `Month: ${label}`}
                  formatter={(val, name, item) => {
                    if (name === 'Penalty (M)' || name === 'Penalty in Rs. (in million)') {
                      return [
                        `₹ ${Number(val).toFixed(2)}`,
                        name
                      ];
                    }
                    if (name === 'Per Unit (Rs./kw)' || name === 'Per Unit (Rs./kwh)' || name?.toLowerCase().includes('per unit')) {
                      return [`₹ ${Number(val).toFixed(2)}`, name];
                    }
                    return [typeof val === 'number' ? Number(val).toFixed(2) : val, name];
                  }}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="monthLabel" height={22} stroke="#888" />
                {/* y1 axis (left): Penalty in Rs. (in million) */}
                <Bar
                  yAxisId="left"
                  dataKey="penaltyInMillion"
                  name="Penalty in Rs. (in million)"
                  fill="#E11D48"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={38}
                />
                {/* y2 axis (right): Per Unit (Rs./kwh) */}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="penaltyPerUnit"
                  name="Per Unit (Rs./kwh)"
                  stroke="#2563EB"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function Last30DaysDeviatedVolumeCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysDevData = async () => {
      setLoading(true);
      try {
        const yesterday = dayjs().subtract(1, 'day');
        const dateFrom = yesterday.subtract(29, 'day').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const list = [];

        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            if (!r.period && !r.date) return;
            const p = r.period || r.date;
            const dayDate = dayjs(p);
            if (!dayDate.isValid() || dayDate.isAfter(yesterday, 'day')) return;

            const dateLabel = dayDate.format('DD-MMM');
            const fullDate = dayDate.format('DD-MMM-YYYY');

            let totalSchedKwh = r.total_schedule_kwh != null
              ? Number(r.total_schedule_kwh)
              : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

            if (totalSchedKwh == null && r.schedule != null) {
              totalSchedKwh = Number(r.schedule) * 250.0 * 96;
            }

            const totalSchedMu = totalSchedKwh != null ? totalSchedKwh / 1000000.0 : 0;

            // Sum of volumes across all bands, excluding the Free Band
            const devVol = Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0)
              + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);

            // Actual Generation (MW)
            const actMw = Number(r.actual != null ? r.actual : r.actual_generation_mw) || 0;

            // % Volume Deviation = Sum of volumes across all bands, excluding the Free Band, divided by Total Schedule
            const devPct = totalSchedKwh && totalSchedKwh > 0 ? (devVol / totalSchedKwh) * 100 : 0;

            list.push({
              period: p,
              dateLabel,
              fullDate,
              devPct: Number(Number(devPct).toFixed(2)),
              deviatedVol: Number(devVol.toFixed(2)),
              actualMw: Number(actMw.toFixed(2)),
              totalScheduleMu: Number(totalSchedMu.toFixed(2)),
              totalScheduleKwh: Number((totalSchedKwh || 0).toFixed(0)),
            });
          });
        }

        if (isMounted) {
          setChartData(list);
        }
      } catch (err) {
        console.error("Error fetching 30-day deviation volume data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysDevData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-30d-dev-volume';

  const tableColumns = [
    { title: 'Date', dataIndex: 'fullDate', key: 'fullDate', render: v => <strong>{v}</strong> },
    { title: '% Volume Deviated in Penalty Band', dataIndex: 'devPct', key: 'devPct', render: v => `${v}%` },
    { title: 'Deviated Vol (MW)', dataIndex: 'deviatedVol', key: 'deviatedVol', render: v => `${v} MW` },
    { title: 'Scheduled Vol (MU)', dataIndex: 'totalScheduleMu', key: 'totalScheduleMu', render: v => `${v} MU` },
    { title: 'Scheduled Units (kWh)', dataIndex: 'totalScheduleKwh', key: 'totalScheduleKwh', render: v => Number(v).toLocaleString('en-IN') },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
            % Volume Deviated in Penalty Band for the last 30 days (daily average)
          </span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Dev_Volume_Last_30Days_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Dev_Volume_Last_30Days_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={55}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${v}%`}
                  domain={[0, 'auto']}
                  label={{
                    value: '% Volume Deviated in Penalty Band',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 120,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullDate ? `Date: ${item.fullDate}` : `Date: ${label}`;
                  }}
                  formatter={(val) => [`${val}%`, '% Volume Deviated in Penalty Band']}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dateLabel" height={22} stroke="#888" />
                <Area
                  type="monotone"
                  dataKey="devPct"
                  name="% Volume Deviated in Penalty Band"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function Last30DaysPenaltyPerUnitCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);
  const [avg30DayRate, setAvg30DayRate] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysData = async () => {
      setLoading(true);
      try {
        const yesterday = dayjs().subtract(1, 'day');
        const dateFrom = yesterday.subtract(29, 'day').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const list = [];
        let totalAllPenalty = 0;
        let totalAllActualKwh = 0;
        let totalAllSchedKwh = 0;

        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            if (!r.period && !r.date) return;
            const p = r.period || r.date;
            const dayDate = dayjs(p);
            if (!dayDate.isValid() || dayDate.isAfter(yesterday, 'day')) return;

            const dateLabel = dayDate.format('DD-MMM');
            const fullDate = dayDate.format('DD-MMM-YYYY');

            const totalPenalty = r.total_penalty != null
              ? Number(r.total_penalty)
              : ((Number(r.oi_penalty) || 0) + (Number(r.ui_penalty) || 0));

            let totalSchedKwh = r.total_schedule_kwh != null
              ? Number(r.total_schedule_kwh)
              : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

            if (totalSchedKwh == null && r.schedule != null) {
              totalSchedKwh = Number(r.schedule) * 250.0 * 96;
            }

            const totalSchedMu = totalSchedKwh != null ? totalSchedKwh / 1000000.0 : 0;

            // Total actual generation units (1 unit = 1 kWh)
            let totalActualKwh = r.total_actual_gen_kwh != null
              ? Number(r.total_actual_gen_kwh)
              : (r.actual != null ? Number(r.actual) * 250.0 * 96 : null);

            const totalActualMu = totalActualKwh != null ? totalActualKwh / 1000000.0 : 0;

            // Penalty per unit = Total Penalty (Rs.) / Total Actual Units (kWh)
            const penaltyPerUnit = totalActualKwh && totalActualKwh > 0
              ? Number((totalPenalty / totalActualKwh).toFixed(3))
              : 0;

            totalAllPenalty += totalPenalty;
            totalAllActualKwh += (totalActualKwh || 0);
            totalAllSchedKwh += (totalSchedKwh || 0);

            list.push({
              period: p,
              dateLabel,
              fullDate,
              totalPenalty: Number(totalPenalty.toFixed(2)),
              penaltyInMillion: Number((totalPenalty / 1000000.0).toFixed(3)),
              totalActualKwh: Number((totalActualKwh || 0).toFixed(2)),
              totalActualMu: Number(totalActualMu.toFixed(2)),
              totalScheduleKwh: Number((totalSchedKwh || 0).toFixed(2)),
              totalScheduleMu: Number(totalSchedMu.toFixed(2)),
              penaltyPerUnit,
            });
          });
        }

        if (isMounted) {
          setChartData(list);
          const avgRate = totalAllActualKwh > 0 ? (totalAllPenalty / totalAllActualKwh) : 0;
          setAvg30DayRate(Number(avgRate.toFixed(3)));
        }
      } catch (err) {
        console.error("Error fetching 30-day penalty per unit data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-30d-penalty-per-unit';

  const tableColumns = [
    { title: 'Date', dataIndex: 'fullDate', key: 'fullDate', render: v => <strong>{v}</strong> },
    { title: 'Per Unit (Rs./kWh)', dataIndex: 'penaltyPerUnit', key: 'penaltyPerUnit', render: v => `₹ ${Number(v).toFixed(3)}` },
    { title: 'Total Penalty (Rs.)', dataIndex: 'totalPenalty', key: 'totalPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
    { title: 'Total Actual (MU)', dataIndex: 'totalActualMu', key: 'totalActualMu', render: v => `${Number(v).toFixed(2)} MU` },
    { title: 'Actual Units (kWh)', dataIndex: 'totalActualKwh', key: 'totalActualKwh', render: v => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) },
    { title: 'Total Scheduled (MU)', dataIndex: 'totalScheduleMu', key: 'totalScheduleMu', render: v => `${Number(v).toFixed(2)} MU` },
    { title: 'Scheduled Units (kWh)', dataIndex: 'totalScheduleKwh', key: 'totalScheduleKwh', render: v => Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }) },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
            Per unit DSM for the last 30 days (daily average)
          </span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Per_Unit_Last_30Days_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Per_Unit_Last_30Days_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={55}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹ ${Number(v).toFixed(2)}`}
                  domain={[0, 'auto']}
                  label={{
                    value: 'Per Unit (Rs./kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 70,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullDate ? `Date: ${item.fullDate}` : `Date: ${label}`;
                  }}
                  formatter={(val, name, item) => {
                    return [
                      `₹ ${Number(val).toFixed(2)} / unit`,
                      'Per Unit (Rs./kWh)'
                    ];
                  }}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dateLabel" height={22} stroke="#888" />
                <Area
                  type="monotone"
                  dataKey="penaltyPerUnit"
                  name="Per Unit (Rs./kWh)"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function OiUiRatioLast15DaysCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);
  const [overallRatio, setOverallRatio] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetch15DaysData = async () => {
      setLoading(true);
      try {
        const yesterday = dayjs().subtract(1, 'day');
        const dateFrom = yesterday.subtract(14, 'day').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const dailyMap = {};
        let totalOiPenalty = 0;
        let totalUiPenalty = 0;

        // Pre-fill dailyMap for 15 days ending on yesterday (24th Sept)
        for (let i = 14; i >= 0; i--) {
          const d = yesterday.subtract(i, 'day');
          const dayKey = d.format('YYYY-MM-DD');
          dailyMap[dayKey] = {
            date: dayKey,
            dateLabel: d.format('DD-MMM'),
            fullDate: d.format('DD-MMM-YYYY'),
            oiPenalty: 0,
            uiPenalty: 0,
            totalPenalty: 0,
          };
        }

        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            const rawDate = r.period || r.date;
            if (!rawDate) return;
            const dayDate = dayjs(rawDate);
            if (!dayDate.isValid() || dayDate.isAfter(yesterday, 'day')) return;

            const dayKey = dayDate.format('YYYY-MM-DD');
            if (!dailyMap[dayKey]) {
              dailyMap[dayKey] = {
                date: dayKey,
                dateLabel: dayDate.format('DD-MMM'),
                fullDate: dayDate.format('DD-MMM-YYYY'),
                oiPenalty: 0,
                uiPenalty: 0,
                totalPenalty: 0,
              };
            }

            const oiSlab = Math.abs(Number(r.oi_0_12) || 0) + Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0);
            const uiSlab = Math.abs(Number(r.ui_0_12) || 0) + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);
            const totP = r.total_penalty != null ? Math.abs(Number(r.total_penalty)) : (oiSlab + uiSlab);

            dailyMap[dayKey].oiPenalty += oiSlab;
            dailyMap[dayKey].uiPenalty += uiSlab;
            dailyMap[dayKey].totalPenalty += totP;
          });
        }

        const sortedDays = Object.keys(dailyMap).sort();
        const list = sortedDays.map((k) => {
          const item = dailyMap[k];
          totalOiPenalty += item.oiPenalty;
          totalUiPenalty += item.uiPenalty;

          const ratio = item.uiPenalty > 0
            ? Number((item.oiPenalty / item.uiPenalty).toFixed(2))
            : (item.oiPenalty === 0 ? 0 : null);

          return {
            period: item.date,
            dateLabel: item.dateLabel,
            fullDate: item.fullDate,
            oiPenalty: Number(item.oiPenalty.toFixed(2)),
            uiPenalty: Number(item.uiPenalty.toFixed(2)),
            totalPenalty: Number(item.totalPenalty.toFixed(2)),
            oiUiRatio: ratio,
            displayRatio: ratio !== null ? ratio.toFixed(2) : (item.oiPenalty > 0 ? 'N/A (UI=0)' : '0.00'),
          };
        });

        if (isMounted) {
          setChartData(list);
          const overall = totalUiPenalty > 0
            ? Number((totalOiPenalty / totalUiPenalty).toFixed(2))
            : null;
          setOverallRatio(overall);
        }
      } catch (err) {
        console.error("Error fetching 15-day OI/UI ratio data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch15DaysData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-15d-oi-ui-ratio';

  const tableColumns = [
    { title: 'Date', dataIndex: 'fullDate', key: 'fullDate', render: v => <strong>{v}</strong> },
    { title: 'OI/UI Ratio', dataIndex: 'displayRatio', key: 'displayRatio', render: v => <span style={{ fontWeight: 600, color: '#1B4F72' }}>{v}</span> },
    { title: 'OI Penalty (₹)', dataIndex: 'oiPenalty', key: 'oiPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
    { title: 'UI Penalty (₹)', dataIndex: 'uiPenalty', key: 'uiPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
    { title: 'Total Penalty (₹)', dataIndex: 'totalPenalty', key: 'totalPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
            OI/UI ratio (last 15 days)
          </span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `OI_UI_Ratio_Last_15Days_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `OI_UI_Ratio_Last_15Days_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={55}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(1)}`}
                  domain={[0, 'auto']}
                  label={{
                    value: 'OI/UI Ratio',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 40,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullDate ? `Date: ${item.fullDate}` : `Date: ${label}`;
                  }}
                  formatter={(val, name, item) => {
                    const p = item?.payload;
                    const oiText = `₹ ${Number(p?.oiPenalty || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    const uiText = `₹ ${Number(p?.uiPenalty || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    const ratioText = val !== null && val !== undefined ? Number(val).toFixed(2) : (p?.oiPenalty > 0 ? 'N/A (UI=0)' : '0.00');
                    return [
                      `${ratioText}`,
                      'OI/UI Ratio'
                    ];
                  }}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <ReferenceLine
                  y={1.0}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                />
                <Brush dataKey="dateLabel" height={22} stroke="#888" />
                <Area
                  type="monotone"
                  dataKey="oiUiRatio"
                  name="OI/UI Ratio"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function DeviatedVolume96Blocks7DaysCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch7DaysBlockData = async () => {
      setLoading(true);
      try {
        const yesterday = dayjs().subtract(1, 'day');
        const dateFrom = yesterday.subtract(6, 'day').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: '15min'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const blocksMap = {};
        for (let b = 1; b <= 96; b++) {
          const startMins = (b - 1) * 15;
          const endMins = b * 15;
          const h1 = String(Math.floor(startMins / 60)).padStart(2, '0');
          const m1 = String(startMins % 60).padStart(2, '0');
          const h2 = String(Math.floor(endMins / 60)).padStart(2, '0');
          const m2 = String(endMins % 60).padStart(2, '0');
          blocksMap[b] = {
            blockNum: b,
            timeLabel: `${h1}:${m1}`,
            timeRange: `${h1}:${m1} - ${h2}:${m2}`,
            totalAbsDevKwh: 0,
            totalSchedKwh: 0,
            totalActualGenMw: 0,
            count: 0
          };
        }

        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            let blockNum = null;
            if (r.block != null && Number(r.block) >= 1 && Number(r.block) <= 96) {
              blockNum = Number(r.block);
            } else if (r.sort_block != null && Number(r.sort_block) >= 1 && Number(r.sort_block) <= 96) {
              blockNum = Number(r.sort_block);
            } else if (r.time_block_index != null) {
              blockNum = Number(r.time_block_index) + 1;
            } else if (r.period) {
              let timeStr = '';
              if (typeof r.period === 'string' && r.period.includes(' ')) {
                timeStr = r.period.split(' ')[1];
              } else if (typeof r.period === 'string' && r.period.includes(':')) {
                timeStr = r.period;
              }
              if (timeStr) {
                const parts = timeStr.split(':');
                const h = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10);
                if (!isNaN(h) && !isNaN(m)) {
                  blockNum = h * 4 + Math.floor(m / 15) + 1;
                }
              }
            }

            if (blockNum && blockNum >= 1 && blockNum <= 96 && blocksMap[blockNum]) {
              const item = blocksMap[blockNum];

              // Schedule: schedule is MW per 15-min block. Energy in kWh = MW * 1000 * 0.25 (or r.total_schedule_kwh if available)
              let schKwh = r.total_schedule_kwh != null
                ? Number(r.total_schedule_kwh)
                : (r.total_schedule != null
                  ? Number(r.total_schedule) * 1000000.0
                  : Math.abs(Number(r.schedule != null ? r.schedule : r.final_schedule_mw) || 0) * 250.0);

              const actMw = Math.abs(Number(r.actual != null ? r.actual : r.actual_generation_mw) || 0);

              // Sum of volumes across all bands, excluding the Free Band (oi/ui slab values are in units/kWh)
              const devVolKwh = Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0)
                + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);

              item.totalAbsDevKwh += devVolKwh;
              item.totalSchedKwh += schKwh;
              item.totalActualGenMw += actMw;
              item.count += 1;
            }
          });
        }

        const list = [];
        for (let b = 1; b <= 96; b++) {
          const item = blocksMap[b];
          let avgDevPct = 0;
          if (item.totalSchedKwh > 0) {
            avgDevPct = (item.totalAbsDevKwh / item.totalSchedKwh) * 100;
          }
          avgDevPct = Number(avgDevPct.toFixed(2));

          const totalSchedMu = item.totalSchedKwh / 1000000.0;
          const devVolMu = item.totalAbsDevKwh / 1000000.0;

          list.push({
            blockNum: item.blockNum,
            blockLabel: `B${item.blockNum}`,
            timeLabel: item.timeLabel,
            timeRange: item.timeRange,
            fullBlockName: `Block ${item.blockNum} (${item.timeLabel})`,
            devPct: avgDevPct,
            totalSched: Number(totalSchedMu.toFixed(3)),
            devVol: Number(devVolMu.toFixed(3)),
            actualMw: item.count > 0 ? Number((item.totalActualGenMw / item.count).toFixed(2)) : 0,
          });
        }

        if (isMounted) {
          setChartData(list);
        }
      } catch (err) {
        console.error("Error fetching 96 blocks deviation volume data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch7DaysBlockData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-96blocks-7d-dev-pct';

  const tableColumns = [
    { title: 'Block', dataIndex: 'blockNum', key: 'blockNum', render: (v, r) => <strong>Block {v} ({r.timeLabel})</strong> },
    { title: '% Volume Deviated in Penalty Band', dataIndex: 'devPct', key: 'devPct', render: v => `${v}%` },
    { title: 'Total Schedule (MU)', dataIndex: 'totalSched', key: 'totalSched', render: v => `${v} MU` },
    { title: 'Deviated Volume (MU)', dataIndex: 'devVol', key: 'devVol', render: v => `${v} MU` },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>% Volume Deviated in Penalty Band for each of the 96 time blocks (7 days)</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Dev_Pct_96Blocks_7Days_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Dev_Pct_96Blocks_7Days_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="blockNum"
                  tick={{ fontSize: 10, fill: '#475569' }}
                  interval={3}
                  tickFormatter={v => {
                    const mins = (Number(v) - 1) * 15;
                    const h = String(Math.floor(mins / 60)).padStart(2, '0');
                    const m = String(mins % 60).padStart(2, '0');
                    return `${h}:${m}`;
                  }}
                  angle={0}
                  textAnchor="middle"
                  height={30}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${v}%`}
                  domain={[0, 'auto']}
                  label={{
                    value: '% Volume Deviated in Penalty Band',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 120,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    const range = item?.timeRange || (item?.timeLabel ? `${item.timeLabel} - ...` : label);
                    return `Time: ${range}`;
                  }}
                  formatter={(val) => [`${val}%`, '% Volume Deviated in Penalty Band']}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush
                  dataKey="blockNum"
                  height={22}
                  stroke="#888"
                  tickFormatter={v => {
                    const mins = (Number(v) - 1) * 15;
                    const h = String(Math.floor(mins / 60)).padStart(2, '0');
                    const m = String(mins % 60).padStart(2, '0');
                    return `${h}:${m}`;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="devPct"
                  name="% Volume Deviated in Penalty Band"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="blockNum"
          />
        )}
      </Spin>
    </Card>
  );
}

function OiUiRatio96Blocks7DaysCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);
  const [overallRatio, setOverallRatio] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetch7DaysOiUiBlockData = async () => {
      setLoading(true);
      try {
        const yesterday = dayjs().subtract(1, 'day');
        const dateFrom = yesterday.subtract(6, 'day').format('YYYY-MM-DD');
        const dateTo = yesterday.format('YYYY-MM-DD');

        const res = await getDsmPenalty({
          date_from: dateFrom,
          date_to: dateTo,
          granularity: '15min'
        }).catch(() => null);

        const rows = res?.data?.chart_data || [];
        const blocksMap = {};
        for (let b = 1; b <= 96; b++) {
          const startMins = (b - 1) * 15;
          const endMins = b * 15;
          const h1 = String(Math.floor(startMins / 60)).padStart(2, '0');
          const m1 = String(startMins % 60).padStart(2, '0');
          const h2 = String(Math.floor(endMins / 60)).padStart(2, '0');
          const m2 = String(endMins % 60).padStart(2, '0');
          blocksMap[b] = {
            blockNum: b,
            timeLabel: `${h1}:${m1}`,
            timeRange: `${h1}:${m1} - ${h2}:${m2}`,
            oiPenalty: 0,
            uiPenalty: 0,
            count: 0
          };
        }

        let totalOiPAll = 0;
        let totalUiPAll = 0;

        if (Array.isArray(rows) && rows.length > 0) {
          rows.forEach((r) => {
            let blockNum = null;
            if (r.block != null && Number(r.block) >= 1 && Number(r.block) <= 96) {
              blockNum = Number(r.block);
            } else if (r.sort_block != null && Number(r.sort_block) >= 1 && Number(r.sort_block) <= 96) {
              blockNum = Number(r.sort_block);
            } else if (r.time_block_index != null) {
              blockNum = Number(r.time_block_index) + 1;
            } else if (r.period) {
              let timeStr = '';
              if (typeof r.period === 'string' && r.period.includes(' ')) {
                timeStr = r.period.split(' ')[1];
              } else if (typeof r.period === 'string' && r.period.includes(':')) {
                timeStr = r.period;
              }
              if (timeStr) {
                const parts = timeStr.split(':');
                const h = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10);
                if (!isNaN(h) && !isNaN(m)) {
                  blockNum = h * 4 + Math.floor(m / 15) + 1;
                }
              }
            }

            if (blockNum && blockNum >= 1 && blockNum <= 96 && blocksMap[blockNum]) {
              const item = blocksMap[blockNum];
              const oiSlab = Math.abs(Number(r.oi_0_12) || 0) + Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0);
              const uiSlab = Math.abs(Number(r.ui_0_12) || 0) + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);
              item.oiPenalty += oiSlab;
              item.uiPenalty += uiSlab;
              totalOiPAll += oiSlab;
              totalUiPAll += uiSlab;
              item.count += 1;
            }
          });
        }

        const list = [];
        for (let b = 1; b <= 96; b++) {
          const item = blocksMap[b];
          const oiP = item.oiPenalty;
          const uiP = item.uiPenalty;
          const ratio = uiP > 0
            ? Number((oiP / uiP).toFixed(2))
            : (oiP === 0 ? 0 : null);

          list.push({
            blockNum: item.blockNum,
            blockLabel: `B${item.blockNum}`,
            timeLabel: item.timeLabel,
            timeRange: item.timeRange,
            fullBlockName: `Block ${item.blockNum} (${item.timeLabel})`,
            oiPenalty: Number(oiP.toFixed(2)),
            uiPenalty: Number(uiP.toFixed(2)),
            oiUiRatio: ratio,
            displayRatio: ratio !== null ? ratio.toFixed(2) : (oiP > 0 ? 'N/A (UI=0)' : '0.00'),
          });
        }

        if (isMounted) {
          setChartData(list);
          const overall = totalUiPAll > 0 ? Number((totalOiPAll / totalUiPAll).toFixed(2)) : null;
          setOverallRatio(overall);
        }
      } catch (err) {
        console.error("Error fetching 96 blocks OI/UI ratio data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch7DaysOiUiBlockData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-96blocks-7d-oi-ui';

  const tableColumns = [
    { title: 'Block', dataIndex: 'blockNum', key: 'blockNum', render: (v, r) => <strong>Block {v} ({r.timeLabel})</strong> },
    { title: 'OI/UI Ratio', dataIndex: 'displayRatio', key: 'displayRatio', render: v => <strong>{v}</strong> },
    { title: 'OI Penalty (₹)', dataIndex: 'oiPenalty', key: 'oiPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
    { title: 'UI Penalty (₹)', dataIndex: 'uiPenalty', key: 'uiPenalty', render: v => `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={DSM_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', marginTop: 24, marginBottom: 16, border: DSM_SUMMARY_BOX_BORDER, overflow: 'hidden' }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
            OI/UI ratio for each of the 96 time blocks (7 days)
          </span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `OiUi_Ratio_96Blocks_7Days_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `OiUi_Ratio_96Blocks_7Days_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="blockNum"
                  tick={{ fontSize: 10, fill: '#475569' }}
                  interval={3}
                  tickFormatter={v => {
                    const mins = (Number(v) - 1) * 15;
                    const h = String(Math.floor(mins / 60)).padStart(2, '0');
                    const m = String(mins % 60).padStart(2, '0');
                    return `${h}:${m}`;
                  }}
                  angle={0}
                  textAnchor="middle"
                  height={30}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(1)}`}
                  domain={[0, 'auto']}
                  label={{
                    value: 'OI/UI Ratio',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 40,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    const range = item?.timeRange || (item?.timeLabel ? `${item.timeLabel} - ...` : label);
                    return `Time: ${range}`;
                  }}
                  formatter={(val, name, item) => {
                    const p = item?.payload;
                    const oiText = `₹ ${Number(p?.oiPenalty || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    const uiText = `₹ ${Number(p?.uiPenalty || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    const ratioText = val !== null && val !== undefined ? Number(val).toFixed(2) : (p?.oiPenalty > 0 ? 'N/A (UI=0)' : '0.00');
                    return [
                      `${ratioText}`,
                      'OI/UI Ratio'
                    ];
                  }}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <ReferenceLine
                  y={1.0}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                />
                <Brush
                  dataKey="blockNum"
                  height={22}
                  stroke="#888"
                  tickFormatter={v => {
                    const mins = (Number(v) - 1) * 15;
                    const h = String(Math.floor(mins / 60)).padStart(2, '0');
                    const m = String(mins % 60).padStart(2, '0');
                    return `${h}:${m}`;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="oiUiRatio"
                  name="OI/UI Ratio"
                  stroke="#2563EB"
                  fill="#93C5FD"
                  fillOpacity={0.4}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="blockNum"
          />
        )}
      </Spin>
    </Card>
  );
}

export default function DsmAnalysisDashboard() {
  const f = useFilters();
  const [activeTab, setActiveTab] = useState('summary');
  const [penaltyData, setPenaltyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [viewSeg, setViewSeg] = useState('chart');
  const [viewSched, setViewSched] = useState('chart');
  const [viewImpact, setViewImpact] = useState('chart');
  const [viewPenalty, setViewPenalty] = useState('chart');
  const [viewDevVol, setViewDevVol] = useState('chart');
  const [viewPerUnit, setViewPerUnit] = useState('chart');
  const [viewOiUiRatio, setViewOiUiRatio] = useState('chart');

  const fetchData = async () => {
    setLoading(true);
    const params = f.apiParams();
    try {
      let apiData = null;
      try {
        const res = await getDsmPenalty(params);
        apiData = res.data;
      } catch (err) {
        console.warn("Backend API getDsmPenalty failed", err);
      }

      const rawChartData = apiData?.chart_data || [];
      const finalChartData = aggregateDsmChartData(rawChartData, f.granularity);

      setPenaltyData({
        kpi_cards: apiData?.kpi_cards || {},
        chart_data: finalChartData,
        raw_chart_data: rawChartData,
        energy_segregation: apiData?.energy_segregation || null,
        heatmap: apiData?.heatmap || [],
        dsm_table_data: apiData?.dsm_table_data || []
      });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (expandedChart) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedChart]);


  const penaltyCards = penaltyData?.kpi_cards || {};
  const chartData = penaltyData?.chart_data || [];
  const processedChartData = React.useMemo(() => {
    return chartData.map(d => {
      const sch = Number(d.schedule) || 0;
      const act = Number(d.actual) || 0;
      const avc = Number(d.avc || d.available_capacity || d.avc_mw || d.capacity) || 0;
      const devMw = act - sch;
      const devPct = d.deviation_pct !== undefined
        ? Number(d.deviation_pct)
        : (avc !== 0 ? (devMw / avc) * 100 : 0);
      const oi_p = Number(d.oi_penalty) || 0;
      const ui_p = Number(d.ui_penalty) || 0;
      return {
        ...d,
        base: Math.min(sch, act),
        deviation: Math.abs(sch - act),
        deviation_mw: devMw,
        deviation_pct: devPct,
        total_penalty: oi_p + ui_p
      };
    });
  }, [chartData]);
  const hm = penaltyData?.heatmap || [];
  const dates = [...new Set(hm.map(h => h.date))];
  const filterSummary = `${f.dateFrom?.format('DD-MMM-YYYY') || ''} to ${f.dateTo?.format('DD-MMM-YYYY') || ''}, ${f.granularity}`;
  const dsmTableData = penaltyData?.dsm_table_data || [];

  const energySegData = React.useMemo(() => {
    // 1. If backend directly provides overall energy_segregation for the entire date range, use it
    if (penaltyData?.energy_segregation) {
      const seg = penaltyData.energy_segregation;
      const seg_gt_20 = Number(seg.seg_gt_20 || 0);
      const seg_15_20 = Number(seg.seg_15_20 || 0);
      const seg_12_15 = Number(seg.seg_12_15 || 0);
      const seg_safe = Number(seg.seg_safe || 0);
      const seg_neg_12_15 = Number(seg.seg_neg_12_15 || 0);
      const seg_neg_15_20 = Number(seg.seg_neg_15_20 || 0);
      const seg_lt_neg_20 = Number(seg.seg_lt_neg_20 || 0);
      const totalScheduleKwh = Number(seg.total_schedule_kwh || 0);

      const val_gt_20 = seg_gt_20 / 1000.0;
      const val_15_20 = seg_15_20 / 1000.0;
      const val_12_15 = seg_12_15 / 1000.0;
      const val_safe = seg_safe / 1000.0;
      const val_neg_12_15 = seg_neg_12_15 / 1000.0;
      const val_neg_15_20 = seg_neg_15_20 / 1000.0;
      const val_lt_neg_20 = seg_lt_neg_20 / 1000.0;

      return [
        { name: '> 20%', value: val_gt_20, pct: totalScheduleKwh > 0 ? (seg_gt_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
        { name: '15% to 20%', value: val_15_20, pct: totalScheduleKwh > 0 ? (seg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
        { name: '12% to 15%', value: val_12_15, pct: totalScheduleKwh > 0 ? (seg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
        { name: '-12% to 12%', value: val_safe, pct: totalScheduleKwh > 0 ? (seg_safe / totalScheduleKwh * 100) : 0, color: '#27AE60' },
        { name: '-15% to -12%', value: val_neg_12_15, pct: totalScheduleKwh > 0 ? (seg_neg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
        { name: '-20% to -15%', value: val_neg_15_20, pct: totalScheduleKwh > 0 ? (seg_neg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
        { name: '< -20%', value: val_lt_neg_20, pct: totalScheduleKwh > 0 ? (seg_lt_neg_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      ];
    }

    // 2. Fallback: calculate from raw, un-bucketed chart rows across the entire range
    const sourceRows = penaltyData?.raw_chart_data || chartData;
    let seg_gt_20 = 0;
    let seg_15_20 = 0;
    let seg_12_15 = 0;
    let seg_safe = 0;
    let seg_neg_12_15 = 0;
    let seg_neg_15_20 = 0;
    let seg_lt_neg_20 = 0;
    let totalScheduleKwh = 0;

    sourceRows.forEach(d => {
      seg_gt_20 += Number(d.oi_20_plus || 0);
      seg_15_20 += Number(d.oi_15_20 || 0);
      seg_12_15 += Number(d.oi_12_15 || 0);
      seg_safe += (Number(d.oi_0_12 || 0) + Math.abs(Number(d.ui_0_12 || 0)));
      seg_neg_12_15 += Math.abs(Number(d.ui_12_15 || 0));
      seg_neg_15_20 += Math.abs(Number(d.ui_15_20 || 0));
      seg_lt_neg_20 += Math.abs(Number(d.ui_20_plus || 0));

      const schedKwh = d.total_schedule_kwh != null
        ? Number(d.total_schedule_kwh)
        : (d.schedule != null ? Number(d.schedule) * 250.0 : 0);
      totalScheduleKwh += schedKwh;
    });

    // Convert values from kWh (Units) to MWh
    const val_gt_20 = seg_gt_20 / 1000.0;
    const val_15_20 = seg_15_20 / 1000.0;
    const val_12_15 = seg_12_15 / 1000.0;
    const val_safe = seg_safe / 1000.0;
    const val_neg_12_15 = seg_neg_12_15 / 1000.0;
    const val_neg_15_20 = seg_neg_15_20 / 1000.0;
    const val_lt_neg_20 = seg_lt_neg_20 / 1000.0;

    return [
      { name: '> 20%', value: val_gt_20, pct: totalScheduleKwh > 0 ? (seg_gt_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '15% to 20%', value: val_15_20, pct: totalScheduleKwh > 0 ? (seg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '12% to 15%', value: val_12_15, pct: totalScheduleKwh > 0 ? (seg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '-12% to 12%', value: val_safe, pct: totalScheduleKwh > 0 ? (seg_safe / totalScheduleKwh * 100) : 0, color: '#27AE60' },
      { name: '-15% to -12%', value: val_neg_12_15, pct: totalScheduleKwh > 0 ? (seg_neg_12_15 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '-20% to -15%', value: val_neg_15_20, pct: totalScheduleKwh > 0 ? (seg_neg_15_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
      { name: '< -20%', value: val_lt_neg_20, pct: totalScheduleKwh > 0 ? (seg_lt_neg_20 / totalScheduleKwh * 100) : 0, color: '#E74C3C' },
    ];
  }, [penaltyData, chartData]);

  const tableDataSeg = React.useMemo(() => {
    return energySegData.map((item, idx) => ({
      ...item,
      key: `seg_${idx}`,
    }));
  }, [energySegData]);

  const columnsSeg = React.useMemo(() => [
    { title: 'Segregation', dataIndex: 'name', key: 'name', width: 200, render: text => <strong>{text}</strong> },
    { title: 'Value (MWh)', dataIndex: 'value', key: 'value', render: v => v != null ? `${Number(v).toFixed(2)} MWh` : '-' },
    { title: 'Percentage (%)', dataIndex: 'pct', key: 'pct', render: v => v != null ? `${Number(v).toFixed(2)}%` : '-' },
  ], []);

  const tableDataSched = React.useMemo(() => formatTableData(processedChartData, f.granularity), [processedChartData, f.granularity]);

  const columnsSched = React.useMemo(() => getTableColumns(f.granularity, [
    { title: 'Scheduled Injection (MW)', dataIndex: 'schedule', render: v => v != null ? Number(v).toFixed(2) : '-' },
    { title: 'Actual Injection (MW)', dataIndex: 'actual', render: v => v != null ? Number(v).toFixed(2) : '-' },
    {
      title: 'Deviation (MW)', dataIndex: 'actual', render: (v, record) => {
        const sch = Number(record.schedule) || 0;
        const act = Number(record.actual) || 0;
        const dev = act - sch;
        return (dev >= 0 ? '+' : '') + dev.toFixed(2);
      }
    }
  ]), [f.granularity]);

  const tableDataImpact = React.useMemo(() => formatTableData(chartData, f.granularity), [chartData, f.granularity]);

  const columnsImpact = React.useMemo(() => getTableColumns(f.granularity, [
    { title: 'Receivable (OI) (₹)', dataIndex: 'receivable', render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    { title: 'Payable (UI) (₹)', dataIndex: 'payable', render: v => v != null ? `₹${Number(Math.abs(v)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    {
      title: 'Net DSM Impact (₹)', key: 'net_impact', render: (_, record) => {
        const netVal = Number(record.receivable || 0) + Number(record.payable || 0);
        const sign = netVal < 0 ? '-' : '';
        return `${sign}₹${Math.abs(netVal).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
      }
    }
  ]), [f.granularity]);

  const tableDataPenalty = React.useMemo(() => formatTableData(chartData, f.granularity), [chartData, f.granularity]);

  const columnsPenalty = React.useMemo(() => getTableColumns(f.granularity, [
    { title: 'OI Penalty (₹)', dataIndex: 'oi_penalty', render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    { title: 'UI Penalty (₹)', dataIndex: 'ui_penalty', render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    {
      title: 'Total Penalty (₹)', key: 'total_penalty', render: (_, record) => {
        const totalVal = Number(record.oi_penalty || 0) + Number(record.ui_penalty || 0);
        return `₹${totalVal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
      }
    }
  ]), [f.granularity]);

  // --- 3 New Graphs for DSM Analysis driven by page date-range filter ---
  // 1. % Volume Deviated
  const chartDevVolData = React.useMemo(() => {
    return chartData.map(r => {
      let totalSchedKwh = r.total_schedule_kwh != null
        ? Number(r.total_schedule_kwh)
        : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

      if (totalSchedKwh == null && r.schedule != null) {
        totalSchedKwh = Number(r.schedule) * 250.0 * (f.granularity === 'hourly' ? 4 : 1);
      }

      // Sum of volumes across all bands, excluding the Free Band (in kWh)
      const devVolKwh = Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0)
        + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);

      const devPct = totalSchedKwh && totalSchedKwh > 0 ? (devVolKwh / totalSchedKwh) * 100 : 0;
      const devVolMu = devVolKwh / 1000000.0;
      const totalSchedMu = totalSchedKwh != null ? totalSchedKwh / 1000000.0 : 0;

      return {
        ...r,
        devPct: Number(devPct.toFixed(2)),
        devVol: Number(devVolMu.toFixed(3)),
        totalSchedMu: Number(totalSchedMu.toFixed(3)),
        totalSchedKwh: Number((totalSchedKwh || 0).toFixed(0)),
      };
    });
  }, [chartData, f.granularity]);

  const tableDataDevVol = React.useMemo(() => formatTableData(chartDevVolData, f.granularity), [chartDevVolData, f.granularity]);

  const columnsDevVol = React.useMemo(() => getTableColumns(f.granularity, [
    { title: '% Volume Deviated in Penalty Band', dataIndex: 'devPct', render: v => v != null ? `${Number(v).toFixed(2)}%` : '-' },
    { title: 'Deviated Volume (MU)', dataIndex: 'devVol', render: v => v != null ? `${Number(v).toFixed(3)} MU` : '-' },
    { title: 'Total Schedule (MU)', dataIndex: 'totalSchedMu', render: v => v != null ? `${Number(v).toFixed(3)} MU` : '-' },
  ]), [f.granularity]);

  // 2. Per Unit DSM
  const chartPerUnitData = React.useMemo(() => {
    return chartData.map(r => {
      const oi_p = Number(r.oi_penalty) || 0;
      const ui_p = Number(r.ui_penalty) || 0;
      const totalPenalty = r.total_penalty != null ? Number(r.total_penalty) : (oi_p + ui_p);

      let totalSchedKwh = r.total_schedule_kwh != null
        ? Number(r.total_schedule_kwh)
        : (r.total_schedule != null ? Number(r.total_schedule) * 1000000.0 : null);

      if (totalSchedKwh == null && r.schedule != null) {
        totalSchedKwh = Number(r.schedule) * 250.0 * (f.granularity === 'hourly' ? 4 : 1);
      }

      const totalSchedMu = totalSchedKwh != null ? totalSchedKwh / 1000000.0 : 0;

      let totalActualKwh = r.total_actual_gen_kwh != null
        ? Number(r.total_actual_gen_kwh)
        : (r.actual != null ? Number(r.actual) * 250.0 * (f.granularity === 'hourly' ? 4 : 1) : null);

      const totalActualMu = totalActualKwh != null ? totalActualKwh / 1000000.0 : 0;

      // Penalty per unit = Total Penalty (Rs.) / Total Actual Units (kWh)
      const penaltyPerUnit = totalActualKwh && totalActualKwh > 0
        ? Number((totalPenalty / totalActualKwh).toFixed(3))
        : 0;

      return {
        ...r,
        totalPenalty: Number(totalPenalty.toFixed(2)),
        penaltyInMillion: Number((totalPenalty / 1000000.0).toFixed(3)),
        totalActualKwh: Number((totalActualKwh || 0).toFixed(0)),
        totalActualMu: Number(totalActualMu.toFixed(3)),
        totalScheduleKwh: Number((totalSchedKwh || 0).toFixed(0)),
        totalScheduleMu: Number(totalSchedMu.toFixed(3)),
        penaltyPerUnit,
      };
    });
  }, [chartData, f.granularity]);

  const tableDataPerUnit = React.useMemo(() => formatTableData(chartPerUnitData, f.granularity), [chartPerUnitData, f.granularity]);

  const columnsPerUnit = React.useMemo(() => getTableColumns(f.granularity, [
    { title: 'Per Unit (Rs./kwh)', dataIndex: 'penaltyPerUnit', render: v => v != null ? `₹ ${Number(v).toFixed(3)}` : '-' },
    { title: 'Total Penalty (₹)', dataIndex: 'totalPenalty', render: v => v != null ? `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    { title: 'Total Actual (MU)', dataIndex: 'totalActualMu', render: v => v != null ? `${Number(v).toFixed(3)} MU` : '-' },
    { title: 'Total Schedule (MU)', dataIndex: 'totalScheduleMu', render: v => v != null ? `${Number(v).toFixed(3)} MU` : '-' },
  ]), [f.granularity]);

  // 3. OI/UI Ratio
  const chartOiUiRatioData = React.useMemo(() => {
    return chartData.map(r => {
      const oiSlab = Math.abs(Number(r.oi_0_12) || 0) + Math.abs(Number(r.oi_12_15) || 0) + Math.abs(Number(r.oi_15_20) || 0) + Math.abs(Number(r.oi_20_plus) || 0);
      const uiSlab = Math.abs(Number(r.ui_0_12) || 0) + Math.abs(Number(r.ui_12_15) || 0) + Math.abs(Number(r.ui_15_20) || 0) + Math.abs(Number(r.ui_20_plus) || 0);
      const totalP = r.total_penalty != null ? Math.abs(Number(r.total_penalty)) : (oiSlab + uiSlab);

      const ratio = uiSlab > 0
        ? Number((oiSlab / uiSlab).toFixed(2))
        : (oiSlab === 0 ? 0 : null);

      return {
        ...r,
        oiPenalty: Number(oiSlab.toFixed(2)),
        uiPenalty: Number(uiSlab.toFixed(2)),
        totalPenalty: Number(totalP.toFixed(2)),
        oiUiRatio: ratio,
        displayRatio: ratio !== null ? ratio.toFixed(2) : (oiSlab > 0 ? 'N/A (UI=0)' : '0.00'),
      };
    });
  }, [chartData]);

  const tableDataOiUiRatio = React.useMemo(() => formatTableData(chartOiUiRatioData, f.granularity), [chartOiUiRatioData, f.granularity]);

  const columnsOiUiRatio = React.useMemo(() => getTableColumns(f.granularity, [
    { title: 'OI/UI Ratio', dataIndex: 'displayRatio', render: v => v != null ? <strong>{v}</strong> : '-' },
    { title: 'OI Penalty (₹)', dataIndex: 'oiPenalty', render: v => v != null ? `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    { title: 'UI Penalty (₹)', dataIndex: 'uiPenalty', render: v => v != null ? `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
    { title: 'Total Penalty (₹)', dataIndex: 'totalPenalty', render: v => v != null ? `₹ ${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-' },
  ]), [f.granularity]);

  const renderCustomBarLabel = (props) => {
    const { x, y, width, height, index } = props;
    const item = energySegData[index];
    if (!item) return null;

    const text = `${item.value.toFixed(2)} (${item.pct.toFixed(2)}%)`;
    const isInside = width > 130;
    const textX = isInside ? x + width - 10 : x + width + 10;
    const textAnchor = isInside ? 'end' : 'start';
    const textColor = isInside ? '#ffffff' : '#333333';

    return (
      <text
        x={textX}
        y={y + height / 2}
        fill={textColor}
        textAnchor={textAnchor}
        dominantBaseline="middle"
        style={{ fontSize: 11, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}
      >
        {text}
      </text>
    );
  };

  const isHourlyOr15Min = f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw';
  const axisInterval = f.granularity === '15min' ? 3 : (f.granularity === '15min-raw' ? 2 : 0);
  const volAxisInterval = f.granularity === '15min' || f.granularity === '15min-raw' ? 7 : (f.granularity === 'hourly' ? 3 : 'preserveStartEnd');
  const axisTickProps = isHourlyOr15Min
    ? { fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }
    : { fontSize: 11, angle: -45, textAnchor: 'end', dy: 15 };
  const formatTick = v => {
    if (!v) return '';
    if (f.granularity === '15min-raw') {
      if (typeof v === 'string' && v.includes(' ')) {
        return v.split(' ')[1];
      }
      return v;
    }
    if (f.granularity === '15min') {
      const num = Number(v);
      if (!isNaN(num) && num >= 1 && num <= 96) {
        const mins = (num - 1) * 15;
        const h = String(Math.floor(mins / 60)).padStart(2, '0');
        const m = String(mins % 60).padStart(2, '0');
        return `${h}:${m}`;
      }
      if (typeof v === 'string' && v.includes(' ')) {
        return v.split(' ')[1];
      }
      return v;
    }
    if (f.granularity === 'hourly') return v;
    const d = dayjs(v);
    if (!d.isValid()) return v;
    if (f.granularity === 'monthly') return d.format('MMM-YY');
    if (f.granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM');
  };

  const brushProps = {};

  return (
    <div id="dashboard-dsm" style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ flex: 1, padding: '0 24px 24px 24px', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Power Trading: DSM Analysis</Typography.Title>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <DsmSummaryTab />
          },
          {
            key: 'analysis',
            label: 'Analysis',
            children: (
              <Spin spinning={loading}>
                {!penaltyData ? <Empty description="Apply filters to load data" /> : (<>
                  {/* Chart 3: Schedule vs Actual (with Shaded Deviation) */}

                  {/* Chart 1: Deviation Band Distribution */}
                  {/* <Card id="chart-dsm-distribution" title="Deviation Band Distribution" size="small" style={expandedChart === 'chart1' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
              extra={
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-distribution', `DSMAnalysis_DeviationBandDistribution_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                  <Button icon={expandedChart === 'chart1' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart1' ? null : 'chart1')} />
                </div>
              }>
              <div style={{ height: expandedChart === 'chart1' ? 'calc(100vh - 120px)' : 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={formatNumberKM} label={{ value: 'Deviation volume (kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                    <Tooltip itemSorter={tooltipSorter} labelFormatter={(label) => {
                      if (f.granularity === '15min-raw') {
                        const d = dayjs(label);
                        return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                      }
                      if (isHourlyOr15Min) return formatAnalysisTooltipLabel(label, f.granularity, null);
                      const d = dayjs(label);
                      if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                      return d.format('DD MMM, YYYY');
                    }} formatter={(v, n) => [`${Math.abs(Number(v)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh`, n]} />
                    <Legend content={renderCustomLegend} />

                    <Bar yAxisId="left" dataKey="oi_0_12" name="Over-Inj: 0-12%" stackId="dsm" fill="#FFC7C7" />
                    <Bar yAxisId="left" dataKey="oi_12_15" name="Over-Inj: 12-15%" stackId="dsm" fill="#FF8B8B" />
                    <Bar yAxisId="left" dataKey="oi_15_20" name="Over-Inj: 15-20%" stackId="dsm" fill="#FF5C5C" />
                    <Bar yAxisId="left" dataKey="oi_20_plus" name="Over-Inj: >20%" stackId="dsm" fill="#C70039" />

                    <Bar yAxisId="left" dataKey="ui_0_12" name="Under-Inj: 0-12%" stackId="dsm" fill="#B3E5FC" />
                    <Bar yAxisId="left" dataKey="ui_12_15" name="Under-Inj: 12-15%" stackId="dsm" fill="#4FC3F7" />
                    <Bar yAxisId="left" dataKey="ui_15_20" name="Under-Inj: 15-20%" stackId="dsm" fill="#29B6F6" />
                    <Bar yAxisId="left" dataKey="ui_20_plus" name="Under-Inj: >20%" stackId="dsm" fill="#0288D1" />

                    <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card> */}

                  {/* Chart 3: Schedule vs Actual (with Shaded Deviation) */}
                  <Card id="chart-dsm-schedule-vs-actual"
                    title="Schedule vs Actual"
                    size="small"
                    style={expandedChart === 'chart3' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewSched} onChange={e => setViewSched(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewSched === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-schedule-vs-actual', `DSMAnalysis_ScheduleVsActual_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataSched, columnsSched, `DSMAnalysis_ScheduleVsActual_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart3' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart3' ? null : 'chart3')} />
                      </div>
                    }>
                    {viewSched === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart3' ? 'calc(100vh - 120px)' : 380 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={processedChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumberKM} label={{ value: 'Power (MW)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                            <Tooltip content={<ScheduleVsActualTooltip granularity={f.granularity} isHourlyOr15Min={isHourlyOr15Min} />} />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />

                            <Area type="monotone" dataKey="base" stackId="deviationBand" fill="transparent" stroke="none" />
                            <Area type="monotone" dataKey="deviation" stackId="deviationBand" name="Deviation" fill="#ccccccb9" stroke="none" legendType="none" />

                            <Line type="monotone" dataKey="schedule" name="Scheduled Injection" stroke="#0052CC" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="actual" name="Actual Injection" stroke="#E74C3C" strokeWidth={2} dot={false} />

                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataSched}
                          columns={columnsSched}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart3' ? 'calc(100vh - 200px)' : 300 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Chart 4: Transposed Heatmap Grid (Dates as Rows, Blocks as Columns) */}
                  {/* <Card id="chart-dsm-heatmap" title="Deviation Heatmap" size="small" style={{ marginBottom: 16 }}
              extra={<DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-heatmap', `DSMAnalysis_Heatmap_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />}>
              <div style={{ overflowX: 'auto', paddingBottom: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `100px repeat(96, 14px)`, gap: 1, fontSize: 10, minWidth: 1450 }}>
                  <div style={{ fontWeight: 700, fontSize: 10, color: '#4b5563', padding: '4px 0' }}>Date \ Block</div>
                  {Array.from({ length: 96 }, (_, blockIdx) => (
                    <div key={blockIdx} style={{
                      textAlign: 'center',
                      fontSize: 8,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f1f5f9',
                      color: '#475569',
                      borderRadius: 2,
                      padding: '2px 0'
                    }} title={`Block ${blockIdx + 1} (${String(Math.floor(blockIdx / 4)).padStart(2, '0')}:${String((blockIdx % 4) * 15).padStart(2, '0')})`}>
                      {blockIdx + 1}
                    </div>
                  ))}

                  {dates.map(dt => (
                    <React.Fragment key={dt}>
                      <div style={{ fontSize: 9, whiteSpace: 'nowrap', fontWeight: 600, display: 'flex', alignItems: 'center', color: '#1e293b' }}>
                        {dayjs(dt).format('DD-MMM-YY')}
                      </div>
                      {Array.from({ length: 96 }, (_, blockIdx) => {
                        const b = hm.find(h => h.date === dt && h.time_block_index === blockIdx);
                        const v = b?.deviation_pct || 0;
                        const devMw = b?.deviation_mw || 0;
                        const intensity = Math.min(Math.abs(v) / 25, 1);
                        let c = '#f8fafc';
                        if (v > 0.5) {
                          c = `rgba(239, 68, 68, ${0.1 + intensity * 0.9})`;
                        } else if (v < -0.5) {
                          c = `rgba(2, 136, 209, ${0.1 + intensity * 0.9})`;
                        }
                        return (
                          <div key={blockIdx}
                            style={{ background: c, height: 16, border: '1px solid #f1f5f9', borderRadius: 1, cursor: 'pointer' }}
                            title={`${dayjs(dt).format('DD MMM YYYY')} | Block ${blockIdx + 1} (${String(Math.floor(blockIdx / 4)).padStart(2, '0')}:${String((blockIdx % 4) * 15).padStart(2, '0')})\nDeviation: ${v?.toFixed(2)}% (${devMw?.toFixed(2)} MW)`}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ marginTop: 15, fontSize: 11, display: 'flex', gap: 20 }}>
                  <span><span style={{ display: 'inline-block', width: 14, height: 14, background: 'rgba(239, 68, 68, 0.6)', verticalAlign: 'middle', borderRadius: 2, marginRight: 4 }} />Over-injection (OI)</span>
                  <span><span style={{ display: 'inline-block', width: 14, height: 14, background: 'rgba(2, 136, 209, 0.6)', verticalAlign: 'middle', borderRadius: 2, marginRight: 4 }} />Under-injection (UI)</span>
                  <span><span style={{ display: 'inline-block', width: 14, height: 14, background: '#f8fafc', border: '1px solid #ddd', verticalAlign: 'middle', borderRadius: 2, marginRight: 4 }} />Negligible</span>
                </div>
              </div>
            </Card> */}

                  {/* Chart 5: DSM Revenue Impact */}
                  <Card id="chart-dsm-revenue-impact"
                    title="DSM (Payables/Receivables)"
                    size="small"
                    style={expandedChart === 'chart5' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewImpact} onChange={e => setViewImpact(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewImpact === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-revenue-impact', `DSMAnalysis_RevenueImpact_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataImpact, columnsImpact, `DSMAnalysis_RevenueImpact_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart5' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart5' ? null : 'chart5')} />
                      </div>
                    }>
                    {viewImpact === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart5' ? 'calc(100vh - 120px)' : 420 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={chartData} stackOffset="sign">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis yAxisId="left" width={90} tickFormatter={formatCurrencyKM} label={{ value: 'Payable / Receivable (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                            <Tooltip
                              itemSorter={tooltipSorter}
                              labelFormatter={(label, payload) => formatAnalysisTooltipLabel(label, f.granularity, payload?.[0]?.payload)}
                              formatter={(v, n) => [v != null ? formatDsmTooltipValue(Math.abs(Number(v))) : '-', n]}
                            />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />

                            <ReferenceLine y={0} stroke="#4b5563" strokeWidth={1} yAxisId="left" />

                            {/* Two-sided bar chart: positive (Receivable) and negative (Payable) bars stacked vertically */}
                            <Bar yAxisId="left" dataKey="receivable" name="Receivable (OI)" stackId="revenue" fill="#27AE60" />
                            <Bar yAxisId="left" dataKey="payable" name="Payable (UI)" stackId="revenue" fill="#E74C3C" />

                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataImpact}
                          columns={columnsImpact}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart5' ? 'calc(100vh - 200px)' : 320 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Chart 6: DSM Penalty Charges */}
                  <Card id="chart-dsm-penalty-charges"
                    title="DSM Penalty Charges"
                    size="small"
                    style={expandedChart === 'chart6' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewPenalty} onChange={e => setViewPenalty(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewPenalty === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-penalty-charges', `DSMAnalysis_PenaltyCharges_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataPenalty, columnsPenalty, `DSMAnalysis_PenaltyCharges_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart6' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart6' ? null : 'chart6')} />
                      </div>
                    }>
                    {viewPenalty === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart6' ? 'calc(100vh - 120px)' : 420 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={processedChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis yAxisId="left" width={90} tickFormatter={formatCurrencyKM} label={{ value: 'Penalty Charges (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                            <Tooltip itemSorter={tooltipSorter} labelFormatter={(label) => {
                              if (f.granularity === '15min-raw') {
                                const d = dayjs(label);
                                return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                              }
                              if (isHourlyOr15Min) return formatAnalysisTooltipLabel(label, f.granularity, null);
                              const d = dayjs(label);
                              if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                              return dayjs(label).format('DD MMM, YYYY');
                            }} formatter={(v, n) => [v != null ? formatDsmTooltipValue(Number(v)) : '-', n]} />
                            <Legend wrapperStyle={{ width: '100%', left: 30 }} />

                            <Bar yAxisId="left" dataKey="total_penalty" name="Total Penalty" fill="#d32f2f" />

                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataPenalty}
                          columns={columnsPenalty}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart6' ? 'calc(100vh - 200px)' : 320 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Chart 2: Distribution of Deviated Units */}
                  <Card id="chart-dsm-segmentation"
                    title="Distribution of Deviated Units"
                    size="small"
                    style={expandedChart === 'chart2' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewSeg} onChange={e => setViewSeg(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewSeg === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-segmentation', `DSMAnalysis_Distribution_of_Deviated_Units_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataSeg, columnsSeg, `DSMAnalysis_Distribution_of_Deviated_Units_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart2' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart2' ? null : 'chart2')} />
                      </div>
                    }>
                    {viewSeg === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart2' ? 'calc(100vh - 120px)' : 350, padding: '10px 20px 10px 10px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={energySegData}
                            layout="vertical"
                            margin={{ top: 5, right: 80, left: 20, bottom: 25 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis
                              type="number"
                              tickFormatter={(v) => formatNumberKM(v).toLowerCase()}
                              label={{ value: 'Deviation in MWh', position: 'insideBottom', offset: -15, style: { fontSize: 11 } }}
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis
                              type="category"
                              dataKey="name"
                              tick={{ fill: '#4b5563', fontWeight: 600, fontSize: 11 }}
                              width={90}
                            />
                            <Tooltip
                              formatter={(v, name, entry) => {
                                const item = entry.payload;
                                return [`${Number(v).toFixed(2)} MWh (${item.pct.toFixed(2)}%)`, 'Energy'];
                              }}
                            />
                            <Bar
                              dataKey="value"
                              barSize={20}
                              label={renderCustomBarLabel}
                            >
                              {energySegData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataSeg}
                          columns={columnsSeg}
                          rowKey="key"
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content' }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* 1. % Volume Deviated in Penalty Band */}
                  <Card id="chart-dsm-percent-volume-deviated"
                    title="% Volume Deviated in Penalty Band"
                    size="small"
                    style={expandedChart === 'chart_dev_vol' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewDevVol} onChange={e => setViewDevVol(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewDevVol === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-percent-volume-deviated', `DSMAnalysis_PercentVolumeDeviated_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataDevVol, columnsDevVol, `DSMAnalysis_PercentVolumeDeviated_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart_dev_vol' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_dev_vol' ? null : 'chart_dev_vol')} />
                      </div>
                    }>
                    {viewDevVol === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart_dev_vol' ? 'calc(100vh - 120px)' : 380 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartDevVolData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#475569' }}
                              tickFormatter={v => `${v}%`}
                              domain={[0, 'auto']}
                              label={{
                                value: '% Volume Deviated in Penalty Band',
                                angle: -90,
                                position: 'insideLeft',
                                dy: 120,
                                style: { fontSize: 12, fill: '#64748b' }
                              }}
                            />
                            <Tooltip
                              itemSorter={tooltipSorter}
                              labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return formatAnalysisTooltipLabel(label, f.granularity, null);
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return dayjs(label).format('DD MMM, YYYY');
                              }}
                              formatter={(val) => [`${val}%`, '% Volume Deviated in Penalty Band']}
                            />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                            <Area
                              type="monotone"
                              dataKey="devPct"
                              name="% Volume Deviated in Penalty Band"
                              stroke="#2563EB"
                              fill="#93C5FD"
                              fillOpacity={0.4}
                              strokeWidth={2.2}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataDevVol}
                          columns={columnsDevVol}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart_dev_vol' ? 'calc(100vh - 200px)' : 320 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* 2. Per Unit DSM */}
                  <Card id="chart-dsm-per-unit"
                    title="Per Unit DSM"
                    size="small"
                    style={expandedChart === 'chart_per_unit' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewPerUnit} onChange={e => setViewPerUnit(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewPerUnit === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-per-unit', `DSMAnalysis_PerUnitDSM_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataPerUnit, columnsPerUnit, `DSMAnalysis_PerUnitDSM_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart_per_unit' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_per_unit' ? null : 'chart_per_unit')} />
                      </div>
                    }>
                    {viewPerUnit === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart_per_unit' ? 'calc(100vh - 120px)' : 380 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartPerUnitData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#475569' }}
                              tickFormatter={v => `₹ ${Number(v).toFixed(2)}`}
                              domain={[0, 'auto']}
                              label={{
                                value: 'Per Unit (Rs./kwh)',
                                angle: -90,
                                position: 'insideLeft',
                                dy: 60,
                                style: { fontSize: 12, fill: '#64748b' }
                              }}
                            />
                            <Tooltip
                              itemSorter={tooltipSorter}
                              labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return formatAnalysisTooltipLabel(label, f.granularity, null);
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return dayjs(label).format('DD MMM, YYYY');
                              }}
                              formatter={(val, name, item) => {
                                return [
                                  `₹ ${Number(val).toFixed(2)} / unit`,
                                  'Per Unit (Rs./kwh)'
                                ];
                              }}
                            />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                            <Area
                              type="monotone"
                              dataKey="penaltyPerUnit"
                              name="Per Unit (Rs./kwh)"
                              stroke="#2563EB"
                              fill="#93C5FD"
                              fillOpacity={0.4}
                              strokeWidth={2.2}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataPerUnit}
                          columns={columnsPerUnit}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart_per_unit' ? 'calc(100vh - 200px)' : 320 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* 3. OI/UI Ratio */}
                  <Card id="chart-dsm-oi-ui-ratio"
                    title="OI/UI Ratio"
                    size="small"
                    style={expandedChart === 'chart_oi_ui' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Radio.Group value={viewOiUiRatio} onChange={e => setViewOiUiRatio(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {viewOiUiRatio === 'chart' ? (
                          <DownloadButton onPdf={() => exportCardAsPdf('chart-dsm-oi-ui-ratio', `DSMAnalysis_OiUiRatio_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableDataOiUiRatio, columnsOiUiRatio, `DSMAnalysis_OiUiRatio_${f.dateFrom?.format('MMMYYYY') || ''}`)} />
                        )}
                        <Button icon={expandedChart === 'chart_oi_ui' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_oi_ui' ? null : 'chart_oi_ui')} />
                      </div>
                    }>
                    {viewOiUiRatio === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart_oi_ui' ? 'calc(100vh - 120px)' : 380 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartOiUiRatioData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis
                              tick={{ fontSize: 11, fill: '#475569' }}
                              tickFormatter={v => `${Number(v).toFixed(1)}`}
                              domain={[0, 'auto']}
                              label={{
                                value: 'OI/UI Ratio',
                                angle: -90,
                                position: 'insideLeft',
                                dy: 40,
                                style: { fontSize: 12, fill: '#64748b' }
                              }}
                            />
                            <Tooltip
                              itemSorter={tooltipSorter}
                              labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return formatAnalysisTooltipLabel(label, f.granularity, null);
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return dayjs(label).format('DD MMM, YYYY');
                              }}
                              formatter={(val, name, item) => {
                                const p = item?.payload;
                                const ratioText = val !== null && val !== undefined ? Number(val).toFixed(2) : (p?.oiPenalty > 0 ? 'N/A (UI=0)' : '0.00');
                                return [
                                  `${ratioText}`,
                                  'OI/UI Ratio'
                                ];
                              }}
                            />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                            <ReferenceLine
                              y={1.0}
                              stroke="#94a3b8"
                              strokeDasharray="3 3"
                            />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                            <Area
                              type="monotone"
                              dataKey="oiUiRatio"
                              name="OI/UI Ratio"
                              stroke="#2563EB"
                              fill="#93C5FD"
                              fillOpacity={0.4}
                              strokeWidth={2.2}
                              dot={false}
                              activeDot={{ r: 6 }}
                              connectNulls={false}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div style={{ padding: '10px 20px', background: '#fff' }}>
                        <Table
                          dataSource={tableDataOiUiRatio}
                          columns={columnsOiUiRatio}
                          rowKey={(r, i) => `${r.period || r.date}_${i}`}
                          size="small"
                          pagination={false}
                          scroll={{ x: 'max-content', y: expandedChart === 'chart_oi_ui' ? 'calc(100vh - 200px)' : 320 }}
                        />
                      </div>
                    )}
                  </Card>

                  {/* Table layout */}
                  {/* <Card id="table-full-dsm" title="Table with Full DSM Data" size="small" style={{ marginTop: 16 }}
              extra={<DownloadButton onPdf={() => exportCardAsPdf('table-full-dsm', `DSMAnalysis_FullData_${f.dateFrom?.format('MMMYYYY') || ''}`, filterSummary)} />}>
              <Table
                columns={dsmTableCols}
                dataSource={dsmTableData}
                rowKey={(r, i) => `${r.date}_${r.time_block}_${i}`}
                size="small"
                pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                scroll={{ x: 'max-content' }}
              />
            </Card> */}
                </>)
                }
              </Spin>
            )
          }
        ]} />
      </div >
      {activeTab === 'analysis' && <GlobalFilterBar onApply={fetchData} />}
    </div >
  );
}
